import {
  legalIdentityOf,
  formatAddress,
  siteAddressIfDifferent,
  smsSegments,
  DomainError,
  type ReminderChannel,
  HU_VAT_RATES,
  buildLine,
  countedLines,
  currency,
  formatMoney,
  fromMinor,
  legalProfile,
  warrantyRequired,
  warrantyUntil,
  missingRequired,
  nextStatuses,
  workOrderNextStep,
  odometerFromInput,
  odometerWarning,
  stockStatus,
  toMinor,
  validateCustomer,
  type ItemKind,
  type PriceMode,
  type WorkOrderLineInput,
  type WorkOrderStatus,
  type VehicleInput,
} from "@szerviz/core";
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { ErrorText } from "../components/ErrorText";
import { Field, TextInput } from "../components/Field";
import { Help } from "../components/Help";
import { Dictate, appendDictation } from "../components/Dictate";
import { StockBadge, useStockTracking } from "./Inventory";
import { Photos } from "../components/Photos";
import { PaymentChoice, PaymentInvoice } from "../components/PaymentInvoice";
import { DeclinedWorkEmail, Recommendations, type TakeProposal } from "../components/Recommendations";
import { missingBuyerData } from "../../../../supabase/functions/_shared/szamlazz";
import { QUOTE_ANSWERED_EVENT } from "../components/QuoteNotices";
import { Plate } from "../components/Plate";
import { CloseReminderDates, CustomerReminderConsent, reminderContactMissing, reminderDatesChanged, reminderDatesOf, type ReminderDates } from "../components/Reminders";
import { MissingNote, useRequired } from "../components/Required";
import { can, useApp, useCan, useFeature } from "../context";
import type { Article, Customer, IntakeLink, Invoice, ItemSuggestion, PaymentMethod, Photo, QuoteChannel, QuoteRequest, Vehicle, WorkOrder, WorkOrderDetail, WorkOrderItem, VehiclePhotoResult, WorkOrderSignature } from "../data";
import { normalizePhone, smsWithPhone } from "../../../../supabase/functions/_shared/messaging";
import { VehicleForm } from "./Vehicles";
import { VehicleDataSheet } from "../components/VehicleDataSheet";
import { DueServices } from "../components/DueServices";
import { PhotoStart, takePendingPhoto } from "../components/VehiclePhoto";
import { WorkshopLogo, useWorkshopLogo } from "../components/WorkshopLogo";
import { PlanTag } from "../components/TrialGuide";
import { Icon } from "../components/Icon";
import { DocHead, DocTotals, KeyValues, MadeWith, Signatures, Stamp, useOperatorLine } from "../components/PrintDocs";
import { IntakeExtrasFields, IntakePanel, IntakeStep, IntakeWalk, PaperSignature, deviceKind, paperLink, PrintCollage, PrintIntakeExtras, SignButton, SignedNote, draftOf, extrasOfDraft, intakeLink, shotsOf, useExtrasRows, useIntakeMessage, useSignatureMatches, type IntakeExtrasDraft, type IntakeShot, type LocalShot } from "../components/Intake";
import { intakeCollage, intakeHandOverOffered, intakeMissing, intakeNeeds, intakePending, intakeSettings, sameIntakeExtras, shortHash } from "@szerviz/core";
import { renderSVG } from "uqr";
import { ReadyNoticeBox } from "../components/ReadyNotice";
import { ChannelSend } from "../components/ChannelSend";
import { MechanicPicker, useMechanicName, useMembers } from "../components/Mechanic";
import { registerLeaveGuard } from "../leaveGuard";
import { lineMechanic, type Role } from "@szerviz/core";
import type { Member, SignedFilter, WorkOrderFilter } from "../data";

const parseNumber = (s: string): number | null => {
  const t = s.replace(/\s/g, "").replace(",", ".");
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
};

function useMoney() {
  const { i18n } = useTranslation();
  const { workshop } = useApp();
  return (minor: number) => formatMoney(minor, workshop.currency, i18n.language);
}

function useDate() {
  const { i18n } = useTranslation();
  // a bare date (YYYY-MM-DD) is read at noon so no time zone moves it to another day
  return (iso: string | null | undefined) =>
    iso ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(new Date(iso.length === 10 ? `${iso}T12:00:00` : iso)) : "";
}

/** Today's local date as YYYY-MM-DD. */
function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Sums the stored line totals (imported lines keep the old program's amounts). */
function sumItems(items: WorkOrderItem[]) {
  const byRate = new Map<number, { net: number; vat: number }>();
  let net = 0;
  let vat = 0;
  let gross = 0;
  for (const i of items) {
    net += i.net;
    vat += i.vat;
    gross += i.gross;
    const r = byRate.get(i.vatRate) ?? { net: 0, vat: 0 };
    byRate.set(i.vatRate, { net: r.net + i.net, vat: r.vat + i.vat });
  }
  return { net, vat, gross, byVatRate: [...byRate.entries()].sort(([a], [b]) => b - a).map(([vatRate, r]) => ({ vatRate, ...r })) };
}

export function StatusChip({ status }: { status: WorkOrderStatus }) {
  const { t } = useTranslation();
  return <span className={`status status-${status}`}>{t(`workOrder.status.${status}`)}</span>;
}

/** What the work order waits for, coloured by urgency. */
export function NextStepChip({ order }: { order: WorkOrder }) {
  const { t } = useTranslation();
  const s = workOrderNextStep(order);
  return <span className={`next-step next-${s.tone}`}>{t(`nextStep.${s.key}`, { count: s.count ?? 0 })}</span>;
}

/** The work order at a glance: next step, lines and total, quote, deadline (the payment is in the closed band). */
function WorkOrderSummary({ order, items, quotes, mechanic }: { order: WorkOrder; items: WorkOrderItem[]; quotes: QuoteRequest[]; mechanic: string | null }) {
  const { t, i18n } = useTranslation();
  const prices = useCan("prices");
  const counted = countedLines(items);
  const gross = counted.reduce((a, i) => a + i.gross, 0);
  const pending = items.filter((i) => i.approval === "pending").length;
  const lastQuote = quotes[quotes.length - 1];
  const day = (iso: string) => new Date(iso).toLocaleDateString(i18n.language);
  const parts = [
    prices ? t("summary.lines", { count: counted.length, gross: formatMoney(gross, "HUF", i18n.language) }) : t("summary.linesNoPrice", { count: counted.length }),
    lastQuote ? (lastQuote.answeredAt ? t("summary.quoteAnswered", { date: day(lastQuote.answeredAt) }) : t("summary.quoteWaiting", { count: pending })) : null,
    order.expectedReadyAt && order.status !== "closed" ? t("summary.expected", { date: day(order.expectedReadyAt) }) : null,
    mechanic ? t("summary.mechanic", { name: mechanic }) : null,
  ].filter(Boolean);
  return (
    <div className="wo-summary" role="status">
      <NextStepChip order={{ ...order, itemCount: items.length, pendingApprovals: pending }} />
      <span className="muted">{parts.join(" · ")}</span>
    </div>
  );
}

/** Work orders as a list; compact = inside a vehicle or customer page. */
export function WorkOrderList({ rows, compact }: { rows: WorkOrder[] | null; compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const { go } = useApp();
  const money = useMoney();
  const date = useDate();
  const mechanicName = useMechanicName(useMembers());
  const prices = useCan("prices");
  if (rows === null) return <p>{t("common.loading")}</p>;
  if (rows.length === 0) return <p className="muted">{t(compact ? "workOrders.noneYet" : "common.noResults")}</p>;
  const open = (id: string) => go("workOrders", id);
  return (
    <div className="table-scroll">
    <table className="list wo-list">
      <thead>
        <tr>
          <th>{t("workOrder.number")}</th>
          <th>{t("workOrder.status")}</th>
          <th>{t("nextStep.column")}</th>
          <th>{t("vehicle.plateNumber")}</th>
          <th>{t("workOrder.customer")}</th>
          <th>{t("workOrder.mechanic")}</th>
          <th>{t("workOrder.date")}</th>
          <th className="num">{t("workOrder.odometerKm")}</th>
          {prices && <th className="num">{t("workOrder.gross")}</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((o) => (
          <tr key={o.id} onClick={() => open(o.id)} tabIndex={0} onKeyDown={(e) => e.key === "Enter" && open(o.id)}>
            <td>
              <strong>{o.number}</strong>
              {o.needsReview && <span className="review-dot" title={t("workOrder.needsReview")} aria-label={t("workOrder.needsReview")} />}
            </td>
            <td>
              <StatusChip status={o.status} />
            </td>
            <td data-label={t("nextStep.column")}>
              <NextStepChip order={o} />
            </td>
            <td className="wo-plate">
              <Plate plate={o.plateNumber ?? ""} country={o.plateCountry} size="sm" />
              {o.vehicleLabel && <div className="muted small">{o.vehicleLabel}</div>}
            </td>
            <td>{o.customerName}</td>
            <td data-label={t("workOrder.mechanic")} className={o.mechanicId ? "mechanic-cell" : "empty"}>{mechanicName(o.mechanicId)}</td>
            <td className="nowrap">{date(o.receivedAt)}</td>
            <td className="num" data-label={t("workOrder.odometerKm")}>{o.odometerKm === null || o.odometerKm === undefined ? "–" : `${o.odometerKm.toLocaleString(i18n.language)} km`}</td>
            {prices && <td className="num">{money(o.gross ?? 0)}</td>}
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

/** On the vehicle page: previous work orders and a button for a new one. */
export function VehicleWorkOrders({ vehicleId }: { vehicleId: string }) {
  const { t } = useTranslation();
  const { store, go } = useApp();
  const [rows, setRows] = useState<WorkOrder[] | null>(null);
  useEffect(() => {
    void store.workOrdersOfVehicle(vehicleId).then(setRows);
  }, [store, vehicleId]);
  return (
    <div className="wo-on-vehicle">
      <div className="page-head">
        <h2>{t("workOrders.title")}</h2>
        <button type="button" className="primary" onClick={() => go("workOrders", `new:${vehicleId}`)}>
          {t("workOrders.newForVehicle")}
        </button>
      </div>
      <WorkOrderList rows={rows} compact />
    </div>
  );
}

export function WorkOrders({ openId }: { openId?: string }) {
  if (openId?.startsWith("new")) return <NewWorkOrder vehicleId={openId.split(":")[1]} />;
  if (openId) return <WorkOrderView key={openId} id={openId} />;
  return <WorkOrderSearch />;
}

/** Where the list starts: a mechanic on his own work orders (owner, 2026-11-04), everybody else on the open ones. */
export const defaultWorkOrderFilter = (role: Role | null): WorkOrderFilter => (role === "mechanic" ? "mine" : "active");

function WorkOrderSearch() {
  const { t } = useTranslation();
  const { store, workshop, go, role } = useApp();
  const [query, setQuery] = useState("");
  const [picked, setFilter] = useState<WorkOrderFilter | null>(null);
  const filter = picked ?? defaultWorkOrderFilter(role);
  // "Aláírt átvételi elismervény: van / nincs" (phone signature, paper record or the photo of the signed paper)
  const [signed, setSigned] = useState<SignedFilter>(null);
  const [rows, setRows] = useState<WorkOrder[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    // the role comes a moment after the page: wait for it, so a mechanic does not see the whole list flash first
    if (!role) return;
    const h = setTimeout(() => {
      store.searchWorkOrders(workshop.id, query, filter, 100, signed).then(
        // the mechanic's own list: what waits for his intake comes first (owner, 2026-11-04)
        (list) => setRows(filter === "mine" ? [...list.filter((o) => intakePending(o)), ...list.filter((o) => !intakePending(o))] : list),
        setError,
      );
    }, 200);
    return () => clearTimeout(h);
  }, [store, workshop.id, query, filter, role, signed]);

  return (
    <section>
      <div className="page-head">
        <h1>
          {t(filter === "mine" ? "workOrders.mineTitle" : "workOrders.title")} <Help id="workOrders.page" />
        </h1>
        <button type="button" className="primary" onClick={() => go("workOrders", "new")}>
          {t("workOrders.new")}
        </button>
      </div>
      <div className="toolbar">
        <input
          className="search"
          type="search"
          placeholder={t("workOrders.searchPlaceholder")}
          aria-label={t("common.search")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="segmented" role="group" aria-label={t("workOrders.filter")}>
          <button type="button" aria-pressed={filter === "mine"} onClick={() => setFilter("mine")}>
            {t("workOrders.filterMine")}
          </button>
          <button type="button" aria-pressed={filter === "active"} onClick={() => setFilter("active")}>
            {t("workOrders.filterActive")}
          </button>
          <button type="button" aria-pressed={filter === "all"} onClick={() => setFilter("all")}>
            {t("workOrders.filterAll")}
          </button>
        </div>
        <div className="signed-filter" role="group" aria-label={t("workOrders.signedFilter")}>
          <span className="signed-filter-label">{t("workOrders.signedFilter")}:</span>
          <span className="segmented">
            {([null, "yes", "no"] as const).map((v) => (
              <button key={String(v)} type="button" aria-pressed={signed === v} onClick={() => setSigned(v)}>
                {t(v === null ? "workOrders.signedAny" : v === "yes" ? "workOrders.signedYes" : "workOrders.signedNo")}
              </button>
            ))}
          </span>
        </div>
      </div>
      <ErrorText error={error} />
      {filter === "mine" && rows?.length === 0 ? (
        <div className="info">
          <p>{t("workOrders.noneMine")}</p>
          <button type="button" onClick={() => setFilter("active")}>
            {t("workOrders.showAllOpen")}
          </button>
        </div>
      ) : filter === "active" && rows?.length === 0 ? (
        <div className="info">
          <p>{t("workOrders.noneActive")}</p>
          <button type="button" onClick={() => setFilter("all")}>
            {t("workOrders.showClosed")}
          </button>
        </div>
      ) : (
        <WorkOrderList rows={rows} />
      )}
    </section>
  );
}

/** New work order: 1. vehicle, 2. customer, 3. odometer and the customer's request. */
function NewWorkOrder({ vehicleId }: { vehicleId?: string }) {
  const { t } = useTranslation();
  const { store, workshop, go, back, role, userId } = useApp();
  const members = useMembers();
  // a mechanic who opens a work order is its responsible mechanic until somebody else is chosen
  const [mechanic, setMechanic] = useState<string | null | undefined>(undefined);
  const mechanicId = mechanic === undefined ? (role === "mechanic" && userId ? userId : null) : mechanic;
  // "Az átvételt a szerelő végzi" (owner, 2026-11-04): offered when somebody else is the responsible mechanic, ticked
  // by default then; the odometer, the intake photos and the signature wait for him
  const [handOver, setHandOver] = useState(true);
  const delegated = intakeHandOverOffered(mechanicId, userId) && handOver;
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [plate, setPlate] = useState("");
  const [found, setFound] = useState<Vehicle[]>([]);
  const [newVehicle, setNewVehicle] = useState(false);
  // work order from the registration photo: the read result fills the new vehicle form
  const [photo, setPhoto] = useState<VehiclePhotoResult | null>(null);
  // after the photo the odometer is the next thing to type
  const [jumpKm, setJumpKm] = useState(false);
  const [customer, setCustomer] = useState<{ id: string; name: string } | null>(null);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [quickCustomer, setQuickCustomer] = useState(false);
  const [km, setKm] = useState("");
  const [lastKm, setLastKm] = useState<number | null>(null);
  const [request, setRequest] = useState("");
  const [readyAt, setReadyAt] = useState(todayIso);
  const [previous, setPrevious] = useState<WorkOrder[] | null>(null);
  // due services chosen to be added when the work order is created
  const [duePicked, setDuePicked] = useState<Record<string, WorkOrderLineInput[]>>({});
  // lines are written only with the items right (a mechanic: the owner's switch)
  const canItems = useCan("items");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  // intake with photos: the photos stay on the phone until the work order is saved, then they are uploaded
  const intakeOn = useFeature("intakePhotos");
  const signOn = useFeature("phoneSignature");
  const intake = intakeSettings(workshop.general);
  const [shots, setShots] = useState<LocalShot[]>([]);
  // what was left in the car (part of the intakePhotos feature, all optional)
  const [extras, setExtras] = useState<IntakeExtrasDraft>(() => draftOf(null));
  const extrasValue = intakeOn ? extrasOfDraft(extras) : null;
  const [uploading, setUploading] = useState<number | null>(null);
  const intakeMessage = useIntakeMessage();

  const chooseVehicle = useCallback(
    async (v: Vehicle) => {
      setVehicle(v);
      setDuePicked({});
      setNewVehicle(false);
      setCustomer(v.customerId ? { id: v.customerId, name: v.customerName ?? "" } : null);
      setLastKm(await store.lastOdometer(v.id));
      setPrevious(await store.workOrdersOfVehicle(v.id));
    },
    [store],
  );

  // a photo already read on the home screen
  useEffect(() => {
    const r = takePendingPhoto();
    if (r) {
      setPhoto(r);
      setNewVehicle(true);
      setJumpKm(true);
    }
  }, []);
  const photoFound = useCallback(
    (v: Vehicle) => {
      setJumpKm(true);
      void chooseVehicle(v);
    },
    [chooseVehicle],
  );

  useEffect(() => {
    if (!vehicleId) return;
    setJumpKm(true);
    void store.getVehicle(vehicleId).then((v) => v && chooseVehicle(v));
  }, [vehicleId, store, chooseVehicle]);

  useEffect(() => {
    if (vehicle || !plate.trim()) {
      setFound([]);
      return;
    }
    const h = setTimeout(() => void store.searchVehicles(workshop.id, plate, 8).then(setFound), 200);
    return () => clearTimeout(h);
  }, [plate, vehicle, store, workshop.id]);

  useEffect(() => {
    if (!customerQuery.trim()) {
      setCustomers([]);
      return;
    }
    const h = setTimeout(() => void store.searchCustomers(workshop.id, customerQuery, 8).then(setCustomers), 200);
    return () => clearTimeout(h);
  }, [customerQuery, store, workshop.id]);

  const kmValue = parseNumber(km);
  const input = {
    vehicleId: vehicle?.id ?? null,
    customerId: customer?.id ?? null,
    odometerKm: odometerFromInput(kmValue),
    expectedReadyAt: readyAt || null,
    customerRequest: request,
    mechanicId,
    intakeByMechanic: delegated,
    ...(intakeOn && extrasValue && !delegated ? { intakeExtras: extrasValue } : {}),
  };
  const missing = missingRequired("workOrder", input, workshop.requiredFields, { intakePending: delegated });
  const req = useRequired("workOrder", input, true);
  const readyRequired = req.isReq("expectedReadyAt");
  // the lower reading warning shows only once the field is left, never while typing
  const [kmLeft, setKmLeft] = useState<{ v: typeof kmValue } | null>(null);
  const warning = kmLeft !== null && kmLeft.v === kmValue ? odometerWarning(input.odometerKm, lastKm) : null;
  // required mode: the Save button stays grey until enough intake photos are taken
  const photosMissing = intakeOn && !delegated ? intakeMissing(intake, shots.length) : null;

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (missing.length > 0 || photosMissing || (!delegated && extrasValue === undefined)) return setError(new DomainError("REQUIRED_MISSING"));
    setBusy(true);
    let sendAfterSign = false;
    // what opens by itself after saving: the pad only on a touch device, the print only on a computer (owner, 2026-11-04)
    const device = deviceKind();
    const padOpens = signOn && device === "touch";
    try {
      const o = await store.createWorkOrder(workshop.id, input);
      // handed to the mechanic: lines, photos, the e-mail, the pad and the receipt all wait for his intake
      if (delegated) {
        go("workOrders", o.id);
        return;
      }
      for (const line of Object.values(duePicked).flat()) await store.saveWorkOrderItem(workshop.id, o.id, line);
      if (intakeOn && shots.length > 0) {
        // a photo that cannot be uploaded now can be taken again on the work order (its panel shows what is missing)
        let left = shots.length;
        setUploading(left);
        let uploaded = 0;
        for (const shot of shots) {
          try {
            await store.addPhoto(workshop.id, { vehicleId: o.vehicleId ?? null, workOrderId: o.id, kind: "intake", intakeView: shot.view }, shot);
            uploaded += 1;
          } catch {
            // counted below
          }
          setUploading(--left);
        }
        // the customer's personal link by e-mail, when the workshop wants it and the customer has an address.
        // With the phone signature on, it goes when the signing step ends on the new work order (signed: the
        // information e-mail; closed without signing: the one that mentions the objection), otherwise now.
        if (intake.send === "ifEmail" && uploaded > 0 && o.customerId) {
          try {
            const c = await store.getCustomer(o.customerId);
            if (c?.email) {
              if (padOpens) sendAfterSign = true;
              else {
                const token = await store.createIntakeLink(o.id);
                await store.sendIntakeEmail(o.id, intakeMessage(o, c.name, intakeLink(token), uploaded, false));
              }
            }
          } catch {
            // not sent: the work order's intake panel says so and has a button to send it
          }
        }
      }
      // the customer is still at the counter: the signature pad opens on the new work order
      if (padOpens) markAutoSign(o.id, sendAfterSign);
      // at a computer the customer signs the printed receipt on paper (setting, on by default)
      if (device === "computer" && workshop.general.intakePrintAfterSave !== false) markAutoPrint(o.id);
      go("workOrders", o.id);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="wo-new">
      <div className="page-head">
        <button type="button" className="link" onClick={() => back("workOrders")}>
          ← {t("common.back")}
        </button>
        <h1>{t("workOrders.new")}</h1>
      </div>

      <div className="step">
        <h2>
          <span className="step-no">1</span> {t("workOrder.vehicle")}
        </h2>
        {vehicle ? (
          <div className="picked">
            <Plate plate={vehicle.plateNumber} country={vehicle.plateCountry} size="lg" />
            <span>{[vehicle.make, vehicle.model].filter(Boolean).join(" ")}</span>
            <button type="button" onClick={() => { setVehicle(null); setPrevious(null); setLastKm(null); }}>
              {t("workOrder.change")}
            </button>
          </div>
        ) : newVehicle ? (
          <VehicleForm
            key={photo ? "photo" : "typed"}
            initial={photo ? ({ plateNumber: "", ...photo.fields } as VehicleInput) : { plateNumber: plate.toUpperCase() }}
            photo={photo}
            onDone={(v) => {
              setPhoto(null);
              if (v) void chooseVehicle(v);
              else setNewVehicle(false);
            }}
          />
        ) : (
          <>
            <PhotoStart
              onFound={photoFound}
              onNew={(r) => {
                setPhoto(r);
                setNewVehicle(true);
                setJumpKm(true);
              }}
            />
            <input
              className="search plate-input"
              type="search"
              autoFocus
              placeholder={t("workOrder.platePlaceholder")}
              aria-label={t("vehicle.plateNumber")}
              value={plate}
              onChange={(e) => setPlate(e.target.value)}
            />
            <ul className="pick-list">
              {found.map((v) => (
                <li key={v.id}>
                  <button type="button" onClick={() => void chooseVehicle(v)}>
                    <Plate plate={v.plateNumber} country={v.plateCountry} size="sm" /> {[v.make, v.model].filter(Boolean).join(" ")}
                    <span className="muted"> · {v.customerName ?? t("vehicle.noOwner")}</span>
                  </button>
                </li>
              ))}
            </ul>
            {plate.trim() && (
              <button type="button" onClick={() => setNewVehicle(true)}>
                + {t("workOrder.newVehicle")}
              </button>
            )}
          </>
        )}
        {previous && previous.length > 0 && (
          <div className="previous">
            <h3>{t("workOrder.previous")}</h3>
            <WorkOrderList rows={previous.slice(0, 5)} compact />
          </div>
        )}
      </div>

      {vehicle && (
        <form className="step" onSubmit={create} noValidate>
          <h2>
            <span className="step-no">2</span> {t("workOrder.customer")}
          </h2>
          {customer ? (
            <div className="picked">
              <strong>{customer.name}</strong>
              <button type="button" onClick={() => setCustomer(null)}>
                {t("workOrder.change")}
              </button>
            </div>
          ) : quickCustomer ? (
            <QuickCustomer
              onDone={(c) => {
                setQuickCustomer(false);
                if (c) setCustomer({ id: c.id, name: c.name });
              }}
            />
          ) : (
            <>
              <input
                type="search"
                className="search"
                placeholder={t("customers.searchPlaceholder")}
                aria-label={t("workOrder.customer")}
                value={customerQuery}
                onChange={(e) => setCustomerQuery(e.target.value)}
              />
              <ul className="pick-list">
                {customers.map((c) => (
                  <li key={c.id}>
                    <button type="button" onClick={() => setCustomer({ id: c.id, name: c.name })}>
                      <strong>{c.name}</strong> <span className="muted">{c.city}</span>
                    </button>
                  </li>
                ))}
              </ul>
              <button type="button" onClick={() => setQuickCustomer(true)}>
                + {t("customers.new")}
              </button>
            </>
          )}

          {intakeOn && !delegated && <IntakeStep no={3} shots={shots} setShots={setShots} />}

          <h2>
            <span className="step-no">{intakeOn && !delegated ? 4 : 3}</span> {t("workOrder.intake")}
          </h2>
          <div className="form-grid">
            {/* who receives the car comes first: with another mechanic the odometer waits for him */}
            <MechanicPicker
              name="newMechanic"
              label={t("workOrder.mechanic")}
              value={mechanicId}
              onChange={setMechanic}
              members={members}
              none={t("mechanic.none")}
              help="workOrder.mechanic"
              required={req.isReq("mechanicId")}
              invalid={missing.some((d) => d.field === "mechanicId")}
            />
            {intakeHandOverOffered(mechanicId, userId) && <HandOverTick checked={handOver} onChange={setHandOver} />}
            {!delegated && <TextInput
              label={t("workOrder.odometerKm")}
              value={km}
              onChange={setKm}
              onBlur={() => setKmLeft({ v: kmValue })}
              type="text"
              autoFocus={jumpKm}
              required
              invalid={!!km && input.odometerKm === null}
              help="workOrder.odometerKm"
            />}
            <TextInput
              label={t("workOrder.expectedReadyAt")}
              value={readyAt}
              onChange={setReadyAt}
              type="date"
              required={readyRequired}
              invalid={missing.some((d) => d.field === "expectedReadyAt")}
              help="workOrder.expectedReadyAt"
            />
            {warning && !delegated && (
              <p className="warn wide" role="status">
                {t("workOrder.odometerLower", { last: String(lastKm) })}
              </p>
            )}
            {!delegated && <DueServices
              vehicle={vehicle}
              km={input.odometerKm}
              picked={canItems ? Object.keys(duePicked) : undefined}
              onPick={!canItems ? undefined : (key, lines) =>
                setDuePicked((p) => {
                  const { [key]: _drop, ...rest } = p;
                  return lines ? { ...rest, [key]: lines } : rest;
                })
              }
            />}
            <Field label={t("workOrder.customerRequest")} wide help="workOrder.customerRequest">
              <textarea value={request} onChange={(e) => setRequest(e.target.value)} aria-label={t("workOrder.customerRequest")} rows={3} />
              <Dictate label={t("workOrder.customerRequest")} onText={(s) => setRequest((r) => appendDictation(r, s))} />
            </Field>
            {intakeOn && !delegated && (
              <div className="wide">
                <IntakeExtrasFields value={extras} onChange={setExtras} />
              </div>
            )}
            <div className="actions wide">
              <ErrorText error={error} />
              <button type="submit" className="primary" disabled={missing.length > 0 || !!photosMissing || (!delegated && extrasValue === undefined) || busy}>
                {t("workOrder.create")}
              </button>
              <MissingNote missing={missing} />
              {photosMissing && <p className="missing" role="status">{t("intake.saveNeeds", { min: photosMissing.needed, done: photosMissing.done })}</p>}
              {!delegated && extrasValue === undefined && <p className="missing" role="status">{t("intake.left.keys")}: {t("intake.left.keysInvalid")}</p>}
              {uploading !== null && uploading > 0 && <p className="info" role="status">{t("photos.uploading", { count: String(uploading) })}</p>}
            </div>
          </div>
        </form>
      )}
    </section>
  );
}

/** Name and phone are enough to start; the rest can be filled in later on the customer page. */
function QuickCustomer({ onDone }: { onDone: (c?: Customer) => void }) {
  const { t } = useTranslation();
  const { store, workshop } = useApp();
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [reminderChannels, setReminderChannels] = useState<ReminderChannel[]>([]);
  const [error, setError] = useState<unknown>(null);
  const missing = missingRequired("customer", { name, mobile }, workshop.requiredFields);
  const save = async () => {
    setError(null);
    try {
      onDone(await store.saveCustomer(workshop.id, validateCustomer({ name, mobile, reminderChannels })));
    } catch (err) {
      setError(err);
    }
  };
  return (
    <div className="form-grid">
      <TextInput label={t("customer.name")} value={name} onChange={setName} required autoFocus />
      <TextInput label={t("customer.mobile")} value={mobile} onChange={setMobile} type="tel" />
      <CustomerReminderConsent form={{ name, mobile, reminderChannels }} onChange={setReminderChannels} />
      <div className="actions wide">
        <ErrorText error={error} />
        <button type="button" className="primary" disabled={missing.length > 0 || reminderContactMissing({ name, mobile, reminderChannels })} onClick={() => void save()}>
          {t("common.save")}
        </button>
        <button type="button" onClick={() => onDone()}>
          {t("common.cancel")}
        </button>
        <MissingNote missing={missing} />
      </div>
    </div>
  );
}

function WorkOrderView({ id }: { id: string }) {
  const { t } = useTranslation();
  const { store, workshop, go, back, role, userId } = useApp();
  const money = useMoney();
  const date = useDate();
  const [detail, setDetail] = useState<WorkOrderDetail | null | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);
  const [reopening, setReopening] = useState(false);
  // an invoiced work order is not reopened (a cancelling invoice comes first)
  const [invoiced, setInvoiced] = useState(false);
  const onInvoice = useCallback((inv: Invoice | null) => setInvoiced(!!inv), []);
  const [reason, setReason] = useState("");
  const [editing, setEditing] = useState<WorkOrderItem | null>(null);
  const members = useMembers();
  const mechanicName = useMechanicName(members);
  // right after "ready": offer the customer notice once (owner, 2026-11-04)
  const [readyOffer, setReadyOffer] = useState(false);
  // three levels (owner, 2026-11-04): what this user may do here; the database enforces the same
  const canItems = useCan("items");
  const prices = useCan("prices");
  const canClose = useCan("close");
  const canQuote = useCan("quote");
  const canNotice = useCan("notice");
  const canInvoice = useCan("invoice");
  // a proposal being taken onto the work order (the item form below is filled from it)
  const [taking, setTaking] = useState<TakeProposal | null>(null);

  const load = useCallback(async () => setDetail(await store.getWorkOrder(id)), [store, id]);
  useEffect(() => void load(), [load]);
  // a customer's answer arriving while the work order is open shows at once
  useEffect(() => {
    const on = () => void load();
    globalThis.addEventListener?.(QUOTE_ANSWERED_EVENT, on);
    return () => globalThis.removeEventListener?.(QUOTE_ANSWERED_EVENT, on);
  }, [load]);

  // The print sheet is rendered only while printing (button or the browser's own print).
  // which document: the browser's own print gives the work order's default (intake receipt or closed sheet)
  const [printing, setPrinting] = useState<PrintDoc | "auto" | null>(null);
  // damage photos of this work order: the intake receipt says how many were taken
  const [damagePhotos, setDamagePhotos] = useState(0);
  const onPhotos = useCallback((list: Photo[]) => setDamagePhotos(list.filter((p) => p.kind === "damage").length), []);
  // intake photos, phone signatures and the customer's link (features "intakePhotos" and "phoneSignature")
  const [intakePhotos, setIntakePhotos] = useState<Photo[]>([]);
  // the photos of the signed paper receipt (kind "document", docType "signedIntake")
  const [paperPhotos, setPaperPhotos] = useState<Photo[]>([]);
  // the intake photos are read before the signature pad may open (the signed content lists them)
  const [intakeLoaded, setIntakeLoaded] = useState(false);
  const loadIntake = useCallback(async () => {
    const all = await store.listPhotos({ workOrderId: id });
    setIntakePhotos(all.filter((p) => p.kind === "intake"));
    setPaperPhotos(all.filter((p) => p.docType === "signedIntake"));
    setIntakeLoaded(true);
  }, [store, id]);
  useEffect(() => void loadIntake(), [loadIntake]);
  const [signatures, setSignatures] = useState<WorkOrderSignature[]>([]);
  const loadSignatures = useCallback(async () => setSignatures(await store.signatures(id)), [store, id]);
  useEffect(() => void loadSignatures(), [loadSignatures]);
  const [lastLink, setLastLink] = useState<IntakeLink | null>(null);
  useEffect(() => void store.lastIntakeLink(id).then(setLastLink, () => setLastLink(null)), [store, id, intakePhotos]);
  const [auto, setAuto] = useState(() => takeAutoSign(id));
  // at a computer the intake receipt prints once after saving (setting intakePrintAfterSave)
  const [autoPrint] = useState(() => takeAutoPrint(id));
  // the mechanic's intake walk-through ("Átvétel megkezdése")
  const [walking, setWalking] = useState(false);
  // opened from the QR code of the printed receipt: the photo of the signed paper
  const [paperJump] = useState(() => (globalThis.location?.hash ?? "").endsWith("/paper"));
  const intakeMessageOf = useIntakeMessage();
  const intake = { on: useFeature("intakePhotos"), settings: intakeSettings(workshop.general) };
  const signOn = useFeature("phoneSignature");
  // the workshop's logo is read in advance, so the print sheet has it at once
  useWorkshopLogo();
  // closing: plainly, or with an invoice (then the payment method is chosen)
  const [withInvoice, setWithInvoice] = useState(false);
  const [payMethod, setPayMethod] = useState<PaymentMethod | null>(null);
  const [invoiceKey, setInvoiceKey] = useState(false);
  useEffect(() => {
    void store.invoiceKeySet(workshop.id).then(setInvoiceKey);
  }, [store, workshop.id]);
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const vehicleId = detail?.order.vehicleId;
  const customerId = detail?.order.customerId;
  useEffect(() => {
    if (vehicleId) store.getVehicle(vehicleId).then(setVehicle, () => setVehicle(null));
  }, [store, vehicleId]);
  // the next due dates offered at closing (reminders feature); saved to the vehicle on closing
  const [dates, setDates] = useState<ReminderDates>(() => reminderDatesOf(null));
  useEffect(() => setDates(reminderDatesOf(vehicle)), [vehicle]);
  const remindersOn = useFeature("reminders");
  useEffect(() => {
    if (customerId) store.getCustomer(customerId).then(setCustomer, () => setCustomer(null));
  }, [store, customerId]);
  useEffect(() => {
    const before = () => flushSync(() => setPrinting((p) => p ?? "auto"));
    const after = () => setPrinting(null);
    globalThis.addEventListener?.("beforeprint", before);
    globalThis.addEventListener?.("afterprint", after);
    return () => {
      globalThis.removeEventListener?.("beforeprint", before);
      globalThis.removeEventListener?.("afterprint", after);
    };
  }, []);
  const print = (doc: PrintDoc | "auto" = "auto") => {
    flushSync(() => setPrinting(doc));
    globalThis.print?.();
  };
  // once the work order, its photos and signatures are read: the browser's print dialog opens (it blocks until
  // closed; the sheet is taken away afterwards even where no afterprint event comes)
  const printedOnce = useRef(false);
  useEffect(() => {
    if (!autoPrint || printedOnce.current || !detail || !intakeLoaded) return;
    printedOnce.current = true;
    const h = setTimeout(() => {
      print("intake");
      setPrinting(null);
    }, 0);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPrint, detail, intakeLoaded]);

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err);
    }
    await load();
  };

  if (detail === undefined) return <p>{t("common.loading")}</p>;
  if (detail === null) return <p className="muted">{t("common.noResults")}</p>;
  const { order, items, events, quotes } = detail;
  const closed = order.status === "closed";
  const totals = sumItems(countedLines(items));
  const pendingLines = items.filter((i) => i.approval === "pending");
  const warranty = warrantyOf(order, items, customer, workshop);
  const shots = shotsOf(intakePhotos);
  const handover = signatures.find((x) => x.kind === "handover");
  // "Átvételre vár": the intake handed to the mechanic is not finished (owner, 2026-11-04)
  const pending = intakePending(order);
  const canIntake = pending && !closed && ((!!userId && userId === order.mechanicId) || canClose);
  const needs = intakeNeeds(order, intake.settings, shots.length, intake.on);
  const blockedText = pending
    ? t("intakeWalk.blocked", { what: (needs.length > 0 ? needs : (["finish"] as const)).map((n) => t(`intakeWalk.need.${n}`, { min: intake.settings.min, done: shots.length })).join(", ") })
    : null;
  /** After "Átvétel kész": the pad opens by itself on a touch device; the customer's e-mail goes as on a new work order. */
  const afterIntake = async () => {
    setWalking(false);
    await load();
    await loadIntake();
    const padOpens = signOn && deviceKind() === "touch";
    const mail = intake.on && intake.settings.send === "ifEmail" && shots.length > 0 && !!customer?.email;
    if (padOpens) setAuto({ sign: true, send: mail });
    else if (mail) {
      try {
        const fresh = await store.getWorkOrder(id);
        const token = await store.createIntakeLink(id);
        await store.sendIntakeEmail(id, intakeMessageOf(fresh?.order ?? order, customer?.name ?? null, intakeLink(token), shots.length, false));
      } catch {
        // not sent: the intake panel says so and has a button to send it
      }
    }
  };

  const buyerMissing = missingBuyerData({ name: customer?.name, postalCode: customer?.postalCode, city: customer?.city, address: customer?.addressLine });
  const closeWork = (payment?: PaymentMethod) => {
    if (items.length === 0 && !globalThis.confirm?.(t("workOrder.closeEmpty"))) return;
    if (!globalThis.confirm?.(t("workOrder.closeConfirm"))) return;
    void run(async () => {
      if (remindersOn && vehicle && reminderDatesChanged(vehicle, dates)) await store.saveVehicleDates(vehicle.id, dates);
      await store.setWorkOrderStatus(order.id, "closed", payment);
      setWithInvoice(false);
      setPayMethod(null);
      // the work order is closed even if the invoice fails; it can be issued again from there
      if (payment) await store.issueInvoice(order.id);
    });
  };


  return (
    <section className="wo">
      <div className="page-head no-print">
        <button type="button" className="link" onClick={() => back("workOrders")}>
          ← {t("common.back")}
        </button>
        <h1>
          {order.number} <StatusChip status={order.status} />
        </h1>
        <button type="button" className="print-btn" onClick={() => print()}>
          <Icon name="print" size={26} />
          {t("workOrder.print")}
        </button>
      </div>


      <WorkOrderSummary order={order} items={items} quotes={quotes} mechanic={mechanicName(order.mechanicId)} />

      {pending && !closed && (
        walking ? (
          <IntakeWalk order={order} photos={intakePhotos} onPhotosChanged={loadIntake} onFinished={afterIntake} onCancel={() => setWalking(false)} />
        ) : (
          <section className="intake-wait no-print" role="status" aria-label={t("intakeWalk.title")}>
            <h2>{t("intakeWalk.title")}</h2>
            <p>{order.mechanicId ? t("intakeWalk.waitText", { name: mechanicName(order.mechanicId) ?? "" }) : t("intakeWalk.waitTextNobody")}</p>
            {canIntake && (
              <button type="button" className="primary big-action" onClick={() => setWalking(true)}>
                {t("intakeWalk.start")}
              </button>
            )}
          </section>
        )
      )}
      {order.intakeByMechanic && order.intakeDoneAt && (
        <p className="muted small no-print">{t("intakeWalk.doneBy", { name: mechanicName(order.intakeDoneBy) ?? "–", date: date(order.intakeDoneAt) })}</p>
      )}

      {order.needsReview && <p className="warn no-print">{t("workOrder.needsReviewText", { reason: order.needsReview === "plate" ? t("workOrder.reviewPlate") : order.needsReview })}</p>}

      <div className="wo-head">
        <a
          className="owner-band vehicle-band"
          href={`#vehicles/${order.vehicleId}`}
          onClick={(e) => {
            e.preventDefault();
            go("vehicles", order.vehicleId!);
          }}
        >
          <span>
            <small>{t("workOrder.vehicle")}</small>
            <strong>
              <Plate plate={order.plateNumber ?? ""} country={order.plateCountry} /> {order.vehicleLabel}
            </strong>
          </span>
          <span className="owner-go no-print">→</span>
        </a>
        <a
          className="owner-band"
          href={`#customers/${order.customerId}`}
          onClick={(e) => {
            e.preventDefault();
            go("customers", order.customerId!);
          }}
        >
          <span>
            <small>{t("workOrder.customer")}</small>
            <strong>{order.customerName}</strong>
          </span>
          <span className="owner-go no-print">→</span>
        </a>
      </div>

      <HeaderForm key={`${order.id}-${order.status}-${pending}`} order={order} closed={closed} onSaved={load} vehicle={vehicle} items={items} members={members} />

      {!pending && <IntakePanel
        order={order}
        customer={customer}
        photos={intakePhotos}
        onPhotosChanged={loadIntake}
        signatures={signatures}
        onSignaturesChanged={() => void loadSignatures()}
        autoSign={auto.sign && !closed && intakeLoaded}
        sendAfterSign={auto.send}
        onLink={setLastLink}
      />}

      {/* signed on paper: recorded with a button, optionally with a photo of the paper (not after a phone signature) */}
      {!pending && !signatures.some((x) => x.kind === "intake") && (
        <PaperSignature order={order} photos={paperPhotos} onChanged={async () => { await load(); await loadIntake(); }} jump={paperJump}
          recorderName={mechanicName(order.paperSigned?.by)} />
      )}

      <Photos vehicleId={order.vehicleId ?? null} workOrderId={order.id} readOnly={closed} defaultKind="damage" onChange={onPhotos} />

      {/* the mechanic's proposals and the declined lines of the vehicle; the administrator takes them onto the work order */}
      {order.vehicleId && !pending && (
        <Recommendations
          vehicleId={order.vehicleId}
          order={order}
          onAdded={load}
          onTake={(p) => {
            setEditing(null);
            setTaking(p);
            globalThis.requestAnimationFrame?.(() => globalThis.document?.querySelector(".item-form")?.scrollIntoView?.({ behavior: "smooth", block: "start" }));
          }}
          takingId={taking?.id ?? null}
        />
      )}

      <h2>{t("workOrder.items")}</h2>
      {items.length === 0 ? (
        <p className="muted">{t("workOrder.noItems")}</p>
      ) : (
        <div className="table-scroll">
          <table className="list items">
            <thead>
              <tr>
                <th>{t("workOrder.item.description")}</th>
                <th>{t("workOrder.item.partNumber")}</th>
                <th className="num">{t("workOrder.item.quantity")}</th>
                {prices && <th className="num">{t("workOrder.item.unitPrice")}</th>}
                {prices && <th className="num">{t("workOrder.item.vatRate")}</th>}
                {prices && <th className="num">{t("workOrder.net")}</th>}
                {prices && <th className="num">{t("workOrder.gross")}</th>}
                {!closed && canItems && <th className="no-print" />}
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className={[editing?.id === i.id && "editing", i.approval === "declined" && "declined"].filter(Boolean).join(" ") || undefined}>
                  <td>
                    <span className={`kind kind-${i.kind}`}>{t(`workOrder.kind.${i.kind}`)}</span> {i.description}
                    {i.optional && <span className="tag-optional"> {t("quote.optional")}</span>}
                    {i.customerSupplied && <span className="tag-supplied"> ({t("workOrder.item.customerSuppliedShort")})</span>}
                    {i.approval && i.approval !== "none" && <ApprovalBadge approval={i.approval} />}
                    {lineMechanic(i, order.mechanicId) && (
                      <div className="line-mechanic small">
                        {t("workOrder.item.mechanicShort", { name: mechanicName(lineMechanic(i, order.mechanicId)) ?? "" })}
                      </div>
                    )}
                  </td>
                  <td data-label={t("workOrder.item.partNumber")} className={i.partNumber ? "part-no" : "empty"}>{i.partNumber}</td>
                  <td className="num" data-label={t("workOrder.item.quantity")}>
                    {i.quantity.toLocaleString()} {i.unit}
                  </td>
                  {prices && (
                    <td className="num" data-label={t("workOrder.item.unitPrice")}>
                      <span>
                        {money(i.unitPrice)} <span className="muted small">{t(`workOrder.priceMode.${i.priceMode}Short`)}</span>
                      </span>
                    </td>
                  )}
                  {prices && <td className="num" data-label={t("workOrder.item.vatRate")}>{i.vatRate}%</td>}
                  {prices && <td className="num" data-label={t("workOrder.net")}>{money(i.net)}</td>}
                  {prices && <td className="num gross" data-label={t("workOrder.gross")}>{money(i.gross)}</td>}
                  {!closed && canItems && (
                    <td className="no-print row-actions">
                      <button type="button" onClick={() => setEditing(i)} aria-label={`${t("workOrder.item.edit")}: ${i.description}`}>
                        {t("workOrder.item.edit")}
                      </button>
                      <button
                        type="button"
                        className="danger"
                        aria-label={`${t("common.delete")}: ${i.description}`}
                        onClick={() => {
                          if (globalThis.confirm?.(t("common.confirmDelete"))) void run(() => store.deleteWorkOrderItem(i.id));
                        }}
                      >
                        {t("common.delete")}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!prices && items.length > 0 && <p className="muted small no-print">{t("workOrder.pricesHidden")}</p>}
      {prices && <div className="totals">
        {totals.byVatRate.length > 1 &&
          totals.byVatRate.map((r) => (
            <div key={r.vatRate} className="muted">
              {t("workOrder.vatLine", { rate: String(r.vatRate), net: money(r.net), vat: money(r.vat) })}
            </div>
          ))}
        <div>
          {t("workOrder.net")}: <strong>{money(totals.net)}</strong>
        </div>
        <div>
          {t("workOrder.vat")}: <strong>{money(totals.vat)}</strong>
        </div>
        <div className="grand">
          {t("workOrder.gross")}: <strong>{money(totals.gross)}</strong>
        </div>
      </div>}

      {!closed && canItems && pending && <p className="muted">{t("intakeWalk.noItems")}</p>}
      {!closed && canItems && !pending && (
        <ItemForm
          key={editing?.id ?? (taking ? `take-${taking.id}` : "new")}
          orderId={order.id}
          orderMechanicId={order.mechanicId ?? null}
          members={members}
          editing={editing}
          prefill={editing ? null : taking}
          onDone={async () => {
            setEditing(null);
            setTaking(null);
            await load();
          }}
        />
      )}

      {/* the quote box under the new line form (owner, 2026-11-01) */}
      {!closed && canQuote && <QuotePanel order={order} pending={pendingLines} quotes={quotes} customer={customer} onChanged={load} onPrint={() => print("quote")} />}
      {canNotice && <DeclinedWorkEmail order={order} items={items} customer={customer} vehicle={vehicle} />}

      <ErrorText error={error} />

      {canNotice && <ReadyNoticeBox order={order} items={items} customer={customer} offer={readyOffer && order.status === "ready"} onOfferDone={() => setReadyOffer(false)} />}

      <div className="actions wo-actions no-print">
        {nextStatuses(order.status).includes("ready") && (
          <button type="button" className="status-btn ready" disabled={pending} onClick={() => void run(async () => { await store.setWorkOrderStatus(order.id, "ready"); setReadyOffer(true); })}>
            {t("workOrder.markReady")}
          </button>
        )}
        {nextStatuses(order.status).includes("open") && (
          <button type="button" onClick={() => void run(() => store.setWorkOrderStatus(order.id, "open"))}>
            {t("workOrder.markOpen")}
          </button>
        )}
        {nextStatuses(order.status).includes("closed") && canClose && (
          <>
            {vehicle && <CloseReminderDates vehicle={vehicle} odometerKm={order.odometerKm ?? null} value={dates} onChange={setDates} />}
            {/* lines still waiting for the customer's yes cannot be billed: ask first */}
            <button type="button" className="primary" onClick={() => closeWork()} disabled={pendingLines.length > 0 || pending}>
              {t("workOrder.close")}
            </button>
            <button type="button" onClick={() => setWithInvoice(true)} disabled={pendingLines.length > 0 || pending || !invoiceKey || buyerMissing.length > 0 || withInvoice}>
              {t("close.withInvoice")}
            </button>
            {!invoiceKey && <p className="muted">{t("invoice.keyMissing")}</p>}
            {invoiceKey && buyerMissing.length > 0 && (
              <p className="missing">{t("invoice.customerMissing", { fields: buyerMissing.map((k) => t(k === "name" ? "customer.name" : `customer.${k}`)).join(", ") })}</p>
            )}
            {withInvoice && (
              <div className="close-invoice">
                <PaymentChoice value={payMethod} onChange={setPayMethod} />
                <div className="actions">
                  <button type="button" className="primary" disabled={!payMethod} onClick={() => payMethod && closeWork(payMethod)}>
                    {t("close.closeAndInvoice")}
                  </button>
                  <button type="button" onClick={() => { setWithInvoice(false); setPayMethod(null); }}>{t("common.cancel")}</button>
                  {!payMethod && <span className="missing">{t("payment.choose")}</span>}
                </div>
              </div>
            )}
            {pendingLines.length > 0 && <p className="missing">{t("quote.closePending", { count: pendingLines.length })}</p>}
          </>
        )}
        {blockedText && <p className="missing" role="status">{blockedText}</p>}
        {!closed && can(role, workshop, "workOrder.delete") && (
          <button
            type="button"
            className="danger"
            onClick={() => {
              if (globalThis.confirm?.(t("common.confirmDelete"))) void run(async () => { await store.deleteWorkOrder(order.id); go("workOrders"); });
            }}
          >
            {t("workOrder.delete")}
          </button>
        )}
      </div>

      {closed && (
        <div className="closed-band no-print">
          <p>
            <strong>{t("workOrder.closedTitle", { date: date(order.closedAt) })}</strong> {t("workOrder.closedText")}
          </p>
          {canInvoice && <PaymentInvoice order={order} customer={customer} onChanged={load} onInvoice={onInvoice} />}
          {handover ? (
            <HandoverNote sig={handover} order={order} customer={customer} shots={shots} />
          ) : (
            signOn && (
              <SignButton kind="handover" order={order} customer={customer} shots={shots} label={t("signature.handoverButton")} onSigned={() => void loadSignatures()} />
            )
          )}
          {warranty.applies && (
            <button type="button" className="print-btn" onClick={() => print("warranty")}>
              <Icon name="warranty" size={26} />
              {t("workOrder.printWarranty")}
            </button>
          )}
          {can(role, workshop, "workOrder.reopen") && invoiced && <p className="muted">{t("workOrder.reopenInvoiced")}</p>}
          {can(role, workshop, "workOrder.reopen") && !invoiced &&
            (reopening ? (
              <div className="actions">
                <TextInput label={t("workOrder.reopenReason")} value={reason} onChange={setReason} help="workOrder.reopen" />
                <button
                  type="button"
                  className="primary"
                  disabled={!reason.trim()}
                  onClick={() => void run(async () => { await store.reopenWorkOrder(order.id, reason); setReopening(false); setReason(""); })}
                >
                  {t("workOrder.reopen")}
                </button>
                <button type="button" onClick={() => setReopening(false)}>
                  {t("common.cancel")}
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setReopening(true)}>
                {t("workOrder.reopen")}
              </button>
            ))}
        </div>
      )}

      {printing && (
        <PrintSheet
          doc={printing === "auto" ? (closed ? "closed" : "intake") : printing}
          order={order}
          items={items}
          quotes={quotes}
          vehicle={vehicle}
          customer={customer}
          damagePhotos={damagePhotos}
          shots={shots}
          signatures={signatures}
          link={lastLink}
          mechanic={mechanicName(order.mechanicId)}
        />
      )}

      <details className="history no-print">
        <summary>{t("workOrder.history")}</summary>
        <ul>
          {events.map((e, n) => (
            <li key={n}>
              {date(e.at)} · {t(`workOrder.event.${e.kind}`)}
              {e.reason && <> · {e.kind === "quote_answered" ? t(`quote.via.${e.reason}`) : e.reason}</>}
            </li>
          ))}
        </ul>
      </details>

      {/* the type data sheet at the bottom of an open work order (owner's request, 2026-11-01) */}
      {!closed && vehicle && (
        <div className="no-print">
          <VehicleDataSheet
            key={`${vehicle.id}-${vehicle.typeId ?? ""}`}
            vehicle={vehicle}
            onChanged={() => void store.getVehicle(vehicle.id).then((v) => v && setVehicle(v))}
          />
        </div>
      )}
    </section>
  );
}

function HeaderForm({ order, closed, onSaved, vehicle, items, members }: { order: WorkOrder; closed: boolean; onSaved: () => Promise<void>; vehicle: Vehicle | null; items: WorkOrderItem[]; members: Member[] | null }) {
  const { t, i18n } = useTranslation();
  const { store, workshop } = useApp();
  const canItems = useCan("items");
  // the administrator or the owner may untick "Az átvételt a szerelő végzi" (then the odometer is written here)
  const canHandOver = useCan("close");
  const wasPending = intakePending(order);
  const [handOver, setHandOver] = useState(wasPending);
  const pendingNow = wasPending && handOver;
  const [mechanic, setMechanic] = useState<string | null>(order.mechanicId ?? null);
  const mechanicName = useMechanicName(members);
  const quotesOn = useFeature("quotes");
  const intakeOn = useFeature("intakePhotos");
  const extrasRows = useExtrasRows();
  const [extras, setExtras] = useState<IntakeExtrasDraft>(() => draftOf(order.intakeExtras));
  const extrasValue = extrasOfDraft(extras);
  const [km, setKm] = useState(order.odometerKm === null ? "" : String(order.odometerKm));
  const [request, setRequest] = useState(order.customerRequest ?? "");
  const [findings, setFindings] = useState(order.findings ?? "");
  const [readyAt, setReadyAt] = useState(order.expectedReadyAt ?? "");
  const [notes, setNotes] = useState(order.notes ?? "");
  const [error, setError] = useState<unknown>(null);
  // autosave (owner's bug report, 2026-11-04): "Mentés…" while saving, "Mentve ✓ 16:05" after
  const [status, setStatus] = useState<{ kind: "saving" } | { kind: "saved"; at: Date } | null>(null);
  // The version of the work order this form was filled from; saving checks it so another tab's newer save is not overwritten.
  const base = useRef(order.updatedAt);
  const kmValue = parseNumber(km);
  const input = {
    vehicleId: order.vehicleId,
    customerId: order.customerId,
    odometerKm: odometerFromInput(kmValue),
    expectedReadyAt: readyAt || null,
    customerRequest: request,
    findings,
    notes,
    mechanicId: mechanic,
    // what was left in the car: only with the feature, so a workshop without it never wipes them (and not while
    // the intake waits: the mechanic's walk-through writes them)
    ...(intakeOn && extrasValue !== undefined && !pendingNow ? { intakeExtras: extrasValue } : {}),
    ...(wasPending && !handOver ? { intakeByMechanic: false } : {}),
  };
  // Imported work orders have no odometer reading; they are closed and read only.
  const missing = order.legacyId ? [] : missingRequired("workOrder", input, workshop.requiredFields, { intakePending: pendingNow });
  const dirty =
    input.odometerKm !== order.odometerKm ||
    readyAt !== (order.expectedReadyAt ?? "") ||
    request !== (order.customerRequest ?? "") ||
    findings !== (order.findings ?? "") ||
    notes !== (order.notes ?? "") ||
    mechanic !== (order.mechanicId ?? null) ||
    handOver !== wasPending ||
    (intakeOn && !pendingNow && (extrasValue === undefined || !sameIntakeExtras(extrasValue, order.intakeExtras)));
  // A reload that brought newer data while nothing was typed here: the form shows the same values, so take its version.
  useEffect(() => {
    if (!dirty) base.current = order.updatedAt;
  }, [order.updatedAt]);
  const date = useDate();
  const req = useRequired("workOrder", input, true);
  const readyRequired = req.isReq("expectedReadyAt");

  // What autosave sends: the valid values; a missing or invalid one keeps its stored value (and stays red here).
  const missingSet = new Set(missing.map((d) => d.field));
  const kmMissing = missingSet.has("odometerKm");
  const savable = {
    ...input,
    odometerKm: kmMissing ? order.odometerKm : input.odometerKm,
    expectedReadyAt: missingSet.has("expectedReadyAt") ? (order.expectedReadyAt ?? null) : input.expectedReadyAt,
    customerRequest: missingSet.has("customerRequest") ? (order.customerRequest ?? "") : request,
    mechanicId: missingSet.has("mechanicId") ? (order.mechanicId ?? null) : mechanic,
    // unticking "Az átvételt a szerelő végzi" needs the odometer: it waits for it
    ...(wasPending && !handOver && kmMissing ? { intakeByMechanic: undefined } : {}),
  };
  const extrasSame = savable.intakeExtras === undefined || sameIntakeExtras(savable.intakeExtras, order.intakeExtras);
  const norm = (x: { odometerKm?: number | null; expectedReadyAt?: string | null; customerRequest?: string | null; findings?: string | null; notes?: string | null; mechanicId?: string | null; intakeByMechanic?: boolean }, same: boolean) =>
    JSON.stringify([x.odometerKm ?? null, x.expectedReadyAt || null, (x.customerRequest ?? "").trim(), (x.findings ?? "").trim(), (x.notes ?? "").trim(), x.mechanicId ?? null,
      x.intakeByMechanic === false, same ? "" : JSON.stringify(savable.intakeExtras ?? null)]);
  const savableSnap = norm(savable, extrasSame);
  const storedSnap = norm({ ...order, intakeByMechanic: undefined }, true);
  // changed values that cannot be saved: asked about before leaving
  const fieldValue = (f: string): string => (f === "odometerKm" ? km.trim() : String((input as Record<string, unknown>)[f] ?? ""));
  const storedValue = (f: string): string =>
    f === "odometerKm" ? (order.odometerKm === null || order.odometerKm === undefined ? "" : String(order.odometerKm)) : String((order as unknown as Record<string, unknown>)[f] ?? "");
  const blocked = [
    ...missing.filter((d) => fieldValue(d.field) !== storedValue(d.field) || (d.field === "odometerKm" && wasPending && !handOver)).map((d) => t(d.labelKey)),
    ...(intakeOn && !pendingNow && extrasValue === undefined ? [t("intake.left.keys")] : []),
  ];
  const latest = useRef({ savable, savableSnap, storedSnap, blocked });
  latest.current = { savable, savableSnap, storedSnap, blocked };
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;
  const inflight = useRef<Promise<void> | null>(null);
  const lastSent = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flush = useCallback(async (): Promise<void> => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    while (inflight.current) await inflight.current;
    const l = latest.current;
    if (l.savableSnap === l.storedSnap || l.savableSnap === lastSent.current) return;
    lastSent.current = l.savableSnap;
    setStatus({ kind: "saving" });
    const run = (async () => {
      try {
        base.current = await store.updateWorkOrder(order.id, l.savable, base.current);
        setError(null);
        setStatus({ kind: "saved", at: new Date() });
        await onSavedRef.current();
      } catch (err) {
        lastSent.current = null;
        setStatus(null);
        setError(err);
      }
    })();
    inflight.current = run;
    await run;
    inflight.current = null;
  }, [store, order.id]);
  /** Autosave about a second after a field is left, a choice changes or a dictation arrives. */
  const schedule = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void flush(), 1000);
  }, [flush]);
  // leaving the screen: what can be saved is saved now, the rest is asked about (App); tab closed or put away: saved
  useEffect(() => {
    if (closed) return;
    const off = registerLeaveGuard(() => {
      void flush();
      return { blocked: latest.current.blocked };
    });
    const hidden = () => {
      if (globalThis.document?.visibilityState === "hidden") void flush();
    };
    const gone = () => void flush();
    const unload = (e: Event) => {
      const l = latest.current;
      if (l.savableSnap === l.storedSnap && l.blocked.length === 0) return;
      void flush();
      e.preventDefault();
    };
    globalThis.document?.addEventListener?.("visibilitychange", hidden);
    globalThis.addEventListener?.("pagehide", gone);
    globalThis.addEventListener?.("beforeunload", unload);
    return () => {
      off();
      globalThis.document?.removeEventListener?.("visibilitychange", hidden);
      globalThis.removeEventListener?.("pagehide", gone);
      globalThis.removeEventListener?.("beforeunload", unload);
      // unmounted (another screen): what is left goes now
      void flush();
    };
  }, [closed, flush]);
  const time = (d: Date) => new Intl.DateTimeFormat(i18n.language, { hour: "2-digit", minute: "2-digit" }).format(d);

  if (closed) {
    return (
      <dl className="wo-facts">
        {order.mechanicId && (
          <div>
            <dt>{t("workOrder.mechanic")}</dt>
            <dd>{mechanicName(order.mechanicId)}</dd>
          </div>
        )}
        <div>
          <dt>{t("workOrder.odometerKm")}</dt>
          <dd>{order.odometerKm ?? "–"}</dd>
        </div>
        {order.expectedReadyAt && (
          <div>
            <dt>{t("workOrder.expectedReadyAt")}</dt>
            <dd>{date(order.expectedReadyAt)}</dd>
          </div>
        )}
        {order.customerRequest && (
          <div className="wide">
            <dt>{t("workOrder.customerRequest")}</dt>
            <dd className="pre">{order.customerRequest}</dd>
          </div>
        )}
        {order.findings && (
          <div className="wide">
            <dt>{t("workOrder.findings")}</dt>
            <dd className="pre">{order.findings}</dd>
          </div>
        )}
        {order.notes && (
          <div className="wide">
            <dt>{t("workOrder.notes")}</dt>
            <dd className="pre">{order.notes}</dd>
          </div>
        )}
        {extrasRows(order.intakeExtras).map(([label, text]) => (
          <div key={label} className="wide">
            <dt>{label}</dt>
            <dd>{text}</dd>
          </div>
        ))}
      </dl>
    );
  }

  const save = async (e: FormEvent) => {
    e.preventDefault();
    await flush();
  };

  return (
    // a field left, a tick or a card chosen: saved about a second later (focusout bubbles in React)
    <form className="form-grid" onSubmit={save} onBlur={() => schedule()} noValidate>
      {/* who works on the car: first, it is what a mechanic looks for on the phone */}
      <MechanicPicker
        name="orderMechanic"
        label={t("workOrder.mechanic")}
        value={mechanic}
        onChange={(v) => { setMechanic(v); schedule(); }}
        members={members}
        none={t("mechanic.none")}
        help="workOrder.mechanic"
        required={req.isReq("mechanicId")}
        invalid={missing.some((d) => d.field === "mechanicId")}
      />
      {wasPending && canHandOver && <HandOverTick checked={handOver} onChange={(v) => { setHandOver(v); schedule(); }} />}
      {!pendingNow && <TextInput label={t("workOrder.odometerKm")} value={km} onChange={(v) => { setKm(v); }} required invalid={!input.odometerKm && input.odometerKm !== 0} help="workOrder.odometerKm" />}
      <DueServices
        vehicle={vehicle}
        km={input.odometerKm}
        items={items}
        onAdd={!canItems || wasPending ? undefined : async (lines) => {
          for (const line of lines) await store.saveWorkOrderItem(workshop.id, order.id, line);
          await onSaved();
        }}
      />
      <TextInput
        label={t("workOrder.expectedReadyAt")}
        value={readyAt}
        onChange={(v) => { setReadyAt(v); }}
        type="date"
        invalid={missing.some((d) => d.field === "expectedReadyAt")}
        required={readyRequired}
        help="workOrder.expectedReadyAt"
      />
      <Field label={t("workOrder.customerRequest")} wide help="workOrder.customerRequest">
        <textarea value={request} onChange={(e) => { setRequest(e.target.value); }} aria-label={t("workOrder.customerRequest")} rows={3} />
        <Dictate label={t("workOrder.customerRequest")} onText={(s) => { setRequest((r) => appendDictation(r, s)); schedule(); }} />
      </Field>
      <Field label={quotesOn ? `${t("workOrder.findings")} (${t("workOrder.findingsSent")})` : t("workOrder.findings")} wide help="workOrder.findings">
        <textarea value={findings} onChange={(e) => { setFindings(e.target.value); }} aria-label={t("workOrder.findings")} rows={3} />
        <Dictate label={t("workOrder.findings")} onText={(s) => { setFindings((r) => appendDictation(r, s)); schedule(); }} />
      </Field>
      <Field label={t("workOrder.notes")} wide>
        <textarea value={notes} onChange={(e) => { setNotes(e.target.value); }} aria-label={t("workOrder.notes")} rows={2} />
        <Dictate label={t("workOrder.notes")} onText={(s) => { setNotes((r) => appendDictation(r, s)); schedule(); }} />
      </Field>
      {intakeOn ? (
        !pendingNow && (
        <div className="wide">
          <IntakeExtrasFields value={extras} onChange={(d) => { setExtras(d); schedule(); }} />
        </div>
        )
      ) : (
        // the feature was switched off later: what was given stays visible
        extrasRows(order.intakeExtras).length > 0 && (
          <dl className="wo-facts wide">
            {extrasRows(order.intakeExtras).map(([label, text]) => (
              <div key={label} className="wide">
                <dt>{label}</dt>
                <dd>{text}</dd>
              </div>
            ))}
          </dl>
        )
      )}
      <div className="actions wide no-print">
        <ErrorText error={error} />
        <span className="autosave muted small" role="status">
          {status?.kind === "saving" ? t("autosave.saving") : status?.kind === "saved" ? t("autosave.saved", { time: time(status.at) }) : ""}
        </span>
        {blocked.length > 0 && <span className="missing">{t("autosave.notSaved", { fields: blocked.join(", ") })}</span>}
        <button type="submit" disabled={!dirty || missing.length > 0 || (intakeOn && !pendingNow && extrasValue === undefined)}>
          {t("common.save")}
        </button>
        <MissingNote missing={missing} />
        {intakeOn && extrasValue === undefined && <span className="missing">{t("intake.left.keys")}: {t("intake.left.keysInvalid")}</span>}
      </div>
    </form>
  );
}

const KINDS: ItemKind[] = ["part", "labour", "other"];

function ItemForm({ orderId, orderMechanicId, members, editing, prefill = null, onDone }: {
  orderId: string;
  /** The work order's responsible mechanic: a labour line without its own mechanic counts for him. */
  orderMechanicId: string | null;
  members: Member[] | null;
  editing: WorkOrderItem | null;
  /** A mechanic's proposal taken onto the work order: the form starts from it, saving closes it. */
  prefill?: TakeProposal | null;
  onDone: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { store, workshop } = useApp();
  // without the prices right (a mechanic with the items switch only) the form has no price: the database
  // takes the article's sale price or leaves it for the administrator
  const showPrices = useCan("prices");
  const canQuote = useCan("quote");
  // null: the responsible mechanic (the default of every new labour line)
  const [lineMechanicId, setLineMechanicId] = useState<string | null>(editing?.mechanicId ?? null);
  const mechanicName = useMechanicName(members);
  const cur = currency(workshop.currency);
  const defaultVat = Number(workshop.general.defaultVatRate ?? 27);
  const hourly = workshop.general.hourlyRateNet;
  const [kind, setKind] = useState<ItemKind>(editing?.kind ?? prefill?.kind ?? "part");
  const [description, setDescription] = useState(editing?.description ?? prefill?.description ?? "");
  const [partNumber, setPartNumber] = useState(editing?.partNumber ?? prefill?.partNumber ?? "");
  const [quantity, setQuantity] = useState(editing ? String(editing.quantity) : prefill?.quantity ? String(prefill.quantity) : "1");
  const [unit, setUnit] = useState(editing?.unit ?? prefill?.unit ?? t(prefill?.kind === "labour" ? "workOrder.unit.hour" : "workOrder.unit.piece"));
  const [price, setPrice] = useState(editing ? String(fromMinor(editing.unitPrice, workshop.currency)) : "");
  const [priceMode, setPriceMode] = useState<PriceMode>(editing?.priceMode ?? "gross");
  const [vatRate, setVatRate] = useState(editing?.vatRate ?? defaultVat);
  const [suggestions, setSuggestions] = useState<ItemSuggestion[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [article, setArticle] = useState<Article | null>(null);
  const [error, setError] = useState<unknown>(null);
  const tracking = useStockTracking();
  const quotesOn = useFeature("quotes");
  const [needsApproval, setNeedsApproval] = useState(!!editing && !!editing.approval && editing.approval !== "none");
  const [optional, setOptional] = useState(editing?.optional ?? false);
  const [customerSupplied, setCustomerSupplied] = useState(editing?.customerSupplied ?? false);
  // a line already answered keeps its answer (a changed price asks again by itself)
  const approval = !needsApproval ? "none" : editing?.approval && editing.approval !== "none" ? editing.approval : "pending";

  // The article of a line being edited (for the stock warning).
  useEffect(() => {
    if (editing?.articleId) void store.getArticle(editing.articleId).then(setArticle);
  }, [editing?.articleId, store]);

  // Typing the description offers articles and lines used before.
  useEffect(() => {
    if (editing || article || description.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    const h = setTimeout(() => {
      void store.itemSuggestions(workshop.id, description).then(setSuggestions);
      if (kind !== "labour") void store.searchArticles(workshop.id, description, 6).then(setArticles);
    }, 250);
    return () => clearTimeout(h);
  }, [description, editing, article, kind, store, workshop.id]);
  useEffect(() => {
    if (article || (description.trim().length < 2 && partNumber.trim().length < 3)) setArticles([]);
  }, [article, description, partNumber]);

  // Typing a part number offers the article with that number (also cross numbers).
  useEffect(() => {
    if (editing || article || kind === "labour" || partNumber.trim().length < 3) return;
    const h = setTimeout(() => void store.searchArticles(workshop.id, partNumber, 6).then(setArticles), 250);
    return () => clearTimeout(h);
  }, [partNumber, editing, article, kind, store, workshop.id]);

  const applyArticle = (a: Article) => {
    setArticle(a);
    setKind("part");
    setDescription(a.name);
    setPartNumber(a.partNumber ?? "");
    setUnit(a.unit ?? t("workOrder.unit.piece"));
    if (a.salePrice !== null && a.salePrice !== undefined) {
      setPrice(String(fromMinor(a.salePrice, workshop.currency)));
      setPriceMode(a.salePriceMode ?? "gross");
    }
    setVatRate(a.vatRate ?? defaultVat);
    setArticles([]);
    setSuggestions([]);
  };

  const pickKind = (k: ItemKind) => {
    setKind(k);
    if (k === "labour") {
      setUnit(t("workOrder.unit.hour"));
      if (!price && typeof hourly === "number") {
        setPrice(String(fromMinor(hourly, workshop.currency)));
        setPriceMode("net");
      }
    } else if (unit === t("workOrder.unit.hour")) setUnit(t("workOrder.unit.piece"));
  };

  const applySuggestion = (s: ItemSuggestion) => {
    setKind(s.kind);
    setDescription(s.description);
    setPartNumber(s.partNumber ?? "");
    setUnit(s.unit ?? "");
    setPrice(String(fromMinor(s.unitPrice, workshop.currency)));
    setPriceMode(s.priceMode);
    setVatRate(s.vatRate);
    setSuggestions([]);
  };

  const q = parseNumber(quantity);
  const p = showPrices ? parseNumber(price) : 0;
  const line: WorkOrderLineInput | null =
    q !== null && !Number.isNaN(q) && q > 0 && p !== null && !Number.isNaN(p) && p >= 0 && description.trim()
      ? { kind, description, partNumber, articleId: kind === "part" && customerSupplied ? null : article?.id ?? editing?.articleId ?? null, unit, quantity: q, unitPrice: toMinor(p, workshop.currency), priceMode, vatRate,
          approval, optional: needsApproval && optional, customerSupplied: kind === "part" && customerSupplied,
          // choosing the responsible mechanic himself is the same as the default (it follows a later change)
          mechanicId: kind === "labour" && lineMechanicId !== orderMechanicId ? lineMechanicId : null }
      : null;
  let preview: { net: number; gross: number } | null = null;
  try {
    preview = line ? buildLine(line) : null;
  } catch {
    preview = null;
  }
  const missingLabels = [
    !description.trim() && t("workOrder.item.description"),
    (q === null || Number.isNaN(q) || q <= 0) && t("workOrder.item.quantity"),
    showPrices && p === null && t("workOrder.item.unitPrice"),
  ].filter(Boolean) as string[];
  // a typed price that is not a number or is negative (#129): red, the reason under the grey button
  const priceBad = p !== null && (Number.isNaN(p) || p < 0);

  const money = useMoney();

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!line) return setError(new DomainError("REQUIRED_MISSING"));
    try {
      const saved = await store.saveWorkOrderItem(workshop.id, orderId, line, editing?.id);
      if (prefill && !editing) await store.closeProposal(prefill.id, saved.id);
      setDescription("");
      setPartNumber("");
      setQuantity("1");
      setPrice("");
      setArticle(null);
      setOptional(false);
      setCustomerSupplied(false);
      setNeedsApproval(false);
      setLineMechanicId(null);
      await onDone();
    } catch (err) {
      setError(err);
    }
  };

  return (
    <form className="item-form form-grid no-print" onSubmit={save} noValidate>
      <h3 className="wide">{t(editing ? "workOrder.item.editTitle" : "workOrder.item.addTitle")}</h3>
      {prefill && !editing && <p className="info wide">{t("proposal.taking", { item: prefill.description })}</p>}
      <div className="segmented wide" role="group" aria-label={t("workOrder.item.kind")}>
        {KINDS.map((k) => (
          <button key={k} type="button" aria-pressed={kind === k} onClick={() => pickKind(k)}>
            {t(`workOrder.kind.${k}`)}
          </button>
        ))}
      </div>
      <Field label={t("workOrder.item.description")} wide required invalid={false} help="workOrder.item.description">
        <div className="owner">
          <input value={description} onChange={(e) => setDescription(e.target.value)} aria-label={t("workOrder.item.description")} />
          <Dictate label={t("workOrder.item.description")} onText={(s) => setDescription((d) => appendDictation(d, s))} />
          {articles.length > 0 && (
            <ul className="suggest articles" aria-label={t("workOrder.item.fromArticles")}>
              {articles.map((a) => (
                <li key={a.id}>
                  <button type="button" onClick={() => applyArticle(a)}>
                    <span className="art-name">{a.name}</span>
                    {a.partNumber && <span className="part-no sub">{a.partNumber}</span>}
                    {showPrices && a.salePrice !== null && a.salePrice !== undefined && <span className="muted"> · {money(a.salePrice)} {t(`workOrder.priceMode.${a.salePriceMode ?? "gross"}Short`)}</span>}
                    {tracking && <> <StockBadge article={a} /></>}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {suggestions.length > 0 && (
            <ul className="suggest">
              {suggestions.map((s) => (
                <li key={s.description}>
                  <button type="button" onClick={() => applySuggestion(s)}>
                    {s.description} {showPrices && <span className="muted">· {money(s.unitPrice)} {t(`workOrder.priceMode.${s.priceMode}Short`)}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Field>
      {kind !== "labour" && (
        <TextInput
          label={t("workOrder.item.partNumber")}
          value={partNumber}
          onChange={(v) => {
            setPartNumber(v);
            // a different number is no longer that article
            if (article && v.trim().toUpperCase() !== (article.partNumber ?? "")) setArticle(null);
          }}
        />
      )}
      <TextInput label={t("workOrder.item.quantity")} value={quantity} onChange={setQuantity} required invalid={quantity.trim() !== "" && !(q !== null && q > 0)} />
      {article && tracking && article.trackStock && (
        <p className={`wide stock-note stock-note-${stockStatus(article, (q ?? 0) - (editing?.articleId === article.id ? editing.quantity : 0))}`}>
          <StockBadge article={article} needed={(q ?? 0) - (editing?.articleId === article.id ? editing.quantity : 0)} />{" "}
          {stockStatus(article, (q ?? 0) - (editing?.articleId === article.id ? editing.quantity : 0)) === "out" && t("stock.notEnough")}
        </p>
      )}
      <TextInput label={t("workOrder.item.unit")} value={unit} onChange={setUnit} />
      {showPrices && <>
      <TextInput label={`${t("workOrder.item.unitPrice")} (${cur.code})`} value={price} onChange={setPrice} required invalid={priceBad} />
      <Field label={t("workOrder.item.priceMode")} help="workOrder.priceMode">
        <select value={priceMode} onChange={(e) => setPriceMode(e.target.value as PriceMode)} aria-label={t("workOrder.item.priceMode")}>
          <option value="gross">{t("workOrder.priceMode.gross")}</option>
          <option value="net">{t("workOrder.priceMode.net")}</option>
        </select>
      </Field>
      <Field label={t("workOrder.item.vatRate")}>
        <select value={vatRate} onChange={(e) => setVatRate(Number(e.target.value))} aria-label={t("workOrder.item.vatRate")}>
          {[...new Set([...HU_VAT_RATES, vatRate])].map((r) => (
            <option key={r} value={r}>
              {r}%
            </option>
          ))}
        </select>
      </Field>
      </>}
      {!showPrices && <p className="muted small wide">{t("workOrder.item.priceByAdmin")}</p>}
      {quotesOn && canQuote && (
        <div className="wide approval-switches">
          <span className="check-row">
            <label className="check">
              <input type="checkbox" checked={needsApproval} onChange={(e) => setNeedsApproval(e.target.checked)} />
              {t("quote.needsApproval")}
            </label>
            <Help id="quote.needsApproval" />
          </span>
          {needsApproval && (
            <label className="check">
              <input type="checkbox" checked={optional} onChange={(e) => setOptional(e.target.checked)} />
              {t("quote.optionalLong")}
            </label>
          )}
        </div>
      )}
      {kind === "labour" && (
        <MechanicPicker
          name="lineMechanic"
          label={t("workOrder.item.mechanic")}
          value={lineMechanicId ?? orderMechanicId}
          onChange={(v) => setLineMechanicId(v)}
          members={members}
          none={orderMechanicId ? undefined : t("mechanic.none")}
          help="workOrder.item.mechanic"
        />
      )}
      {kind === "labour" && orderMechanicId && lineMechanicId === null && (
        <p className="muted small wide">{t("workOrder.item.mechanicDefault", { name: mechanicName(orderMechanicId) ?? "" })}</p>
      )}
      {/* rare: a part the customer brought; small and below the quote ticks (owner, 2026-11-01) */}
      {kind === "part" && (
        <label className="check tiny wide">
          <input type="checkbox" checked={customerSupplied} onChange={(e) => setCustomerSupplied(e.target.checked)} />
          {t("workOrder.item.customerSupplied")}
        </label>
      )}
      <div className="actions wide">
        {preview && showPrices && (
          <span className="line-preview">
            {t("workOrder.net")}: <strong>{money(preview.net)}</strong> · {t("workOrder.gross")}: <strong>{money(preview.gross)}</strong>
          </span>
        )}
        <ErrorText error={error} />
        <button type="submit" className="primary" disabled={!line || missingLabels.length > 0 || priceBad}>
          {t(editing ? "common.save" : "workOrder.item.add")}
        </button>
        {(editing || prefill) && (
          <button type="button" onClick={() => void onDone()}>
            {t("common.cancel")}
          </button>
        )}
        {missingLabels.length > 0 && (description || price) && <p className="missing">{t("required.stillMissing", { fields: missingLabels.join(", ") })}</p>}
        {priceBad && <p className="missing">{t("error.INVALID_PRICE.action")}</p>}
      </div>
    </form>
  );
}

function ApprovalBadge({ approval }: { approval: NonNullable<WorkOrderItem["approval"]> }) {
  const { t } = useTranslation();
  return <span className={`approval approval-${approval}`}> {t(`quote.approval.${approval}`)}</span>;
}

/** The customer's link to the quote page. */
export const quoteLink = (token: string) => `${globalThis.location?.origin ?? ""}${globalThis.location?.pathname ?? "/"}#quote/${token}`;

/**
 * Asking the customer about the lines marked for approval: a link to send
 * (share, e-mail or copy), or a yes recorded by phone.
 */
function QuotePanel({ order, pending, quotes, customer, onChanged, onPrint }: { order: WorkOrder; pending: WorkOrderItem[]; quotes: QuoteRequest[]; customer: Customer | null; onChanged: () => Promise<void>; onPrint: () => void }) {
  const { t } = useTranslation();
  const { store, workshop } = useApp();
  const money = useMoney();
  const date = useDate();
  const on = useFeature("quotes");
  const [error, setError] = useState<unknown>(null);
  const [phone, setPhone] = useState(false);
  const [yes, setYes] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const [copied, setCopied] = useState(false);
  const smsOn = useFeature("sms");
  // the feature off (e.g. after the Profi week of the trial) with lines still waiting: only the answer by phone,
  // so the work order can be closed; a link already sent keeps working for the customer until it expires
  if (pending.length === 0 && (!on || quotes.length === 0)) return null;
  const last = quotes[0];
  const open = last && last.channel === "link" && !last.answeredAt && last.expiresAt > new Date().toISOString() ? last : null;
  const link = open ? quoteLink(open.token) : null;
  const sum = pending.reduce((a, i) => a + i.gross, 0);
  const findings = order.findings?.trim() ?? "";

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await onChanged();
    } catch (err) {
      setError(err);
    }
  };
  const share = async () => {
    if (!link) return;
    const shareText = t("quote.shareText", { number: order.number, plate: order.plateNumber ?? "" });
    const text = findings ? `${t("quote.findingsText", { findings })}\n\n${shareText}` : shareText;
    if (globalThis.navigator?.share) await globalThis.navigator.share({ title: t("quote.title"), text, url: link }).catch(() => undefined);
    else {
      await globalThis.navigator?.clipboard?.writeText(link);
      setCopied(true);
    }
  };
  // addressed by name like the quote page (#113), signed with the workshop's name and contact
  const customerName = customer?.name?.trim() ?? "";
  const greeting = customerName ? t("quote.page.greeting", { name: customerName }) : t("quote.page.greetingNoName");
  const contact = (key: string) => {
    const v = workshop.general[key];
    return typeof v === "string" ? v.trim() : "";
  };
  // only the name: the phone, e-mail and address are in the clickable contacts box under the letter (2026-11-04)
  const signature = workshop.name;
  const mailBody = link ? [greeting, t("quote.mailBody", { plate: order.plateNumber ?? "", link }), signature].filter(Boolean).join("\n\n") : "";
  // the discovered fault goes with the quote, right after the link
  const body = link && findings ? mailBody.replace(link, `${link}\n\n${t("quote.findingsText", { findings })}`) : mailBody;
  // short message for SMS and chat apps: text, fault, link
  // and the workshop's number in the +36 form phones link (send-quote adds it the same way)
  const shortText = link
    ? smsWithPhone([t("quote.shareText", { number: order.number, plate: order.plateNumber ?? "" }), findings ? t("quote.findingsText", { findings }) : "", link].filter(Boolean).join("\n"),
        contact("workshopPhone"), t("contacts.smsPhone", { phone: "{{phone}}" }))
    : "";
  // the main phone number is "mobile" (one field on the form); "phone" is the older second one
  const phoneRaw = customer?.mobile || customer?.phone || "";
  const phoneDigits = phoneRaw ? normalizePhone(phoneRaw) : null;
  // e-mail: the whole letter with a button; SMS: the short text with the link
  const messageFor = (channel: QuoteChannel) =>
    channel === "email"
      ? { subject: t("quote.mailSubject", { number: order.number }), text: body, link: link!, buttonLabel: t("quote.mailButton") }
      : { subject: "", text: shortText, link: link!, buttonLabel: "" };

  return (
    <section className="quote-panel no-print" aria-label={t("quote.title")}>
      <h3>
        {t("quote.title")}
        <Help id="quote.panel" /> <PlanTag featureKey="quotes" />
      </h3>
      {!on && <p className="warn">{t("quote.offNote")}</p>}
      {pending.length > 0 && <p className="warn">{t("quote.pendingSummary", { count: pending.length, sum: money(sum) })}</p>}
      {pending.length > 0 && <p className="muted">{order.findings?.trim() ? t("quote.findingsShown") : t("quote.findingsEmpty")}</p>}
      {last?.answeredAt && (
        <p className="ok-band">
          {t("quote.answered", { date: date(last.answeredAt), via: t(`quote.via.${last.channel}`), count: last.approvedIds.length, total: last.itemIds.length })}
        </p>
      )}
      {open && <p className="muted">{t("quote.waiting", { date: date(open.createdAt), until: date(open.expiresAt) })}</p>}
      <div className="actions">
        <button type="button" className="print-btn" onClick={onPrint}>
          <Icon name="quote" size={26} />
          {t("workOrder.printQuote")}
        </button>
      </div>
      <ErrorText error={error} />
      {pending.length > 0 && !phone && (
        <div className="actions">
          {!on ? null : link ? (
            <>
              {/* one button to every ticked channel (owner, 2026-11-04); the chat apps stay beside it */}
              <ChannelSend type="quote" customer={customer} buttonLabel={t("quote.sendChannels")} send={(c) => store.sendQuote(order.id, c, messageFor(c))} />
              <button type="button" onClick={() => void share()}>
                {t("quote.share")}
              </button>
              {phoneDigits && (
                <a className="button" href={`https://wa.me/${phoneDigits}?text=${encodeURIComponent(shortText)}`} target="_blank" rel="noreferrer">
                  {t("quote.whatsapp")}
                </a>
              )}
              <a className="button" href={`viber://forward?text=${encodeURIComponent(shortText)}`}>
                {t("quote.viber")}
              </a>
              {!smsOn && phoneDigits && (
                <a className="button" href={`sms:+${phoneDigits}?body=${encodeURIComponent(shortText)}`}>
                  {t("quote.smsApp")}
                </a>
              )}
              <input className="quote-link" readOnly value={link} aria-label={t("quote.link")} onFocus={(e) => e.target.select()} />
              {copied && <span className="ok">{t("quote.copied")}</span>}
              <details className="ready-preview">
                <summary>{t("readyNotice.preview")}</summary>
                <p className="ps-cap">{t("readyNotice.previewSms", { count: smsSegments(shortText).segments })}</p>
                <pre className="msg-text">{shortText}</pre>
              </details>
            </>
          ) : (
            <button type="button" className="primary" onClick={() => void run(() => store.createQuoteRequest(order.id))}>
              {t("quote.send")}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setYes(pending.map((i) => i.id));
              setPhone(true);
            }}
          >
            {t("quote.byPhone")}
          </button>
        </div>
      )}
      {phone && (
        <div className="phone-answer">
          <p>{t("quote.byPhoneIntro")}</p>
          {pending.map((i) => (
            <label key={i.id} className="check">
              <input type="checkbox" checked={yes.includes(i.id)} onChange={(e) => setYes((y) => (e.target.checked ? [...y, i.id] : y.filter((x) => x !== i.id)))} />
              {i.description} · {money(i.gross)}
            </label>
          ))}
          <TextInput label={t("quote.note")} value={note} onChange={setNote} />
          <div className="actions">
            <button
              type="button"
              className="primary"
              onClick={() =>
                void run(async () => {
                  await store.recordQuoteAnswer(order.id, yes, note);
                  setPhone(false);
                  setNote("");
                })
              }
            >
              {t("quote.saveAnswer")}
            </button>
            <button type="button" onClick={() => setPhone(false)}>
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/** The printed documents of a work order (owner, 2026-11-03: the four sheets of "A · Munkalap"). */
export type PrintDoc = "intake" | "closed" | "warranty" | "quote";

/** A text setting of the workshop, trimmed, or null. */
function generalText(workshop: { general: Record<string, unknown> }, k: string) {
  const v = workshop.general[k];
  return typeof v === "string" && v.trim() ? v.trim() : null;
}

/**
 * Whether a closed work order carries a warranty, and its facts: the statutory one (a consumer above
 * the threshold of the legal profile) or the workshop's own terms. Same condition on the closed sheet,
 * on the warranty card and for its print button.
 */
export function warrantyOf(order: WorkOrder, items: WorkOrderItem[], customer: Customer | null, workshop: { general: Record<string, unknown>; currency: string }) {
  const profile = legalProfile(generalText(workshop, "legalCountry") ?? "HU");
  const own = generalText(workshop, "warrantyText");
  const closed = order.status === "closed";
  const gross = sumItems(countedLines(items)).gross;
  // a work order without a company customer is treated as a consumer's (the safe side)
  const statutory = closed && warrantyRequired(profile, { consumer: !customer?.isCompany, grossMinor: gross, currency: workshop.currency });
  const handover = order.closedAt ?? order.readyAt;
  const months = statutory && profile.consumerWarranty ? profile.consumerWarranty.months : null;
  const until = months && handover ? warrantyUntil(handover, months) : null;
  return { profile, own, statutory, handover, months, until, applies: closed && (statutory || !!own) };
}

/** The latest quote link that still works (its QR code and address go on the printed quote). */
function openQuoteLink(quotes: QuoteRequest[]) {
  const now = new Date().toISOString();
  const q = quotes.find((x) => x.channel === "link" && x.expiresAt > now);
  return q ? { link: quoteLink(q.token), quote: q } : null;
}

/** Lines of the work order that were put to the customer as a quote. */
const quotedLines = (items: WorkOrderItem[]) => items.filter((i) => i.approval && i.approval !== "none");

/**
 * The printed work order: a sheet of its own (A4, normal print sizes), only visible when printing; the
 * screen view is hidden then. One component, four documents: the intake receipt (open work order), the
 * closed work order, the warranty card and the quote.
 */
function PrintSheet({ doc, order, items: allItems, quotes, vehicle, customer, damagePhotos, shots = [], signatures = [], link = null, mechanic = null }: {
  doc: PrintDoc;
  order: WorkOrder;
  items: WorkOrderItem[];
  quotes: QuoteRequest[];
  vehicle: Vehicle | null;
  customer: Customer | null;
  damagePhotos: number;
  /** Intake photos (the collage of the intake receipt). */
  shots?: IntakeShot[];
  /** Phone signatures: the intake one on the intake receipt, the handover one on the closed sheet. */
  signatures?: WorkOrderSignature[];
  /** The customer's intake link ("+N a linken"). */
  link?: IntakeLink | null;
  /** The responsible mechanic's name ("Szerelő: …" on the work order sheets, not on the quote). */
  mechanic?: string | null;
}) {
  const { t, i18n } = useTranslation();
  const { workshop } = useApp();
  const money = useMoney();
  const date = useDate();
  // a mechanic without the prices right prints the lines without money
  const prices = useCan("prices");
  const dateTime = (iso: string | null | undefined) =>
    iso ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso)) : "";
  const g = (k: string) => generalText(workshop, k);
  const legalLine = useOperatorLine(legalIdentityOf(workshop.general), workshop.name);
  // declined lines are not part of the work order any more (the quote shows them struck through)
  const items = doc === "quote" ? allItems : countedLines(allItems);
  const counted = countedLines(allItems);
  const totals = sumItems(counted);
  const answered = quotes.find((q) => q.answeredAt);
  // "1151 Budapest, Mélyfúró u. 3." (a foreign country named at the end)
  const address = customer
    ? formatAddress({ country: customer.country ?? "HU", postalCode: customer.postalCode ?? "", city: customer.city ?? "", street: customer.addressLine ?? "" }, { language: i18n.language })
    : "";
  const phone = customer ? [customer.mobile, customer.phone].filter(Boolean).join(", ") : "";
  const vehicleName = vehicle ? [vehicle.make, vehicle.model, vehicle.typeDescription].filter(Boolean).join(" ") : order.vehicleLabel ?? "";
  const w = warrantyOf(order, allItems, customer, workshop);
  const kmText = order.odometerKm === null ? null : `${order.odometerKm.toLocaleString(i18n.language)} km`;
  const plate = order.plateNumber ? <Plate plate={order.plateNumber} country={order.plateCountry} size="sm" /> : null;
  const qr = doc === "quote" ? openQuoteLink(quotes) : null;
  // the customer's phone signature sits above the customer's signature line (paper signature otherwise)
  const phoneSig = doc === "intake" ? signatures.find((x) => x.kind === "intake") : doc === "closed" ? signatures.find((x) => x.kind === "handover") : undefined;

  const title = t(
    doc === "closed" ? "workOrder.print.closedTitle" : doc === "warranty" ? "workOrder.print.warrantyDocTitle" : doc === "quote" ? "workOrder.print.quoteTitle" : "workOrder.print.openTitle",
  );
  const mechanicLine = mechanic ? `${t("workOrder.print.mechanic")}: ${mechanic}` : null;
  const meta =
    doc === "intake"
      ? [`${t("workOrder.print.receivedAt")}: ${dateTime(order.receivedAt)}`, mechanicLine].filter(Boolean).join(" · ")
      : doc === "closed"
        ? [`${t("workOrder.print.receivedAt")}: ${date(order.receivedAt)}`, order.closedAt && `${t("workOrder.print.closedAt")}: ${date(order.closedAt)}`, mechanicLine].filter(Boolean).join(" · ")
        : doc === "quote"
          ? [`${t("workOrder.print.issuedAt")}: ${date(new Date().toISOString())}`, qr && `${t("workOrder.print.validUntil")}: ${date(qr.quote.expiresAt)}`].filter(Boolean).join(" · ")
          : t("warranty.ofOrder", { number: order.number });

  const head = (
    <DocHead
      logo={<WorkshopLogo className="ps-logo" />}
      shop={workshop.name}
      // who runs the workshop, small under the trade name: legal name (once when it is the trade name), seat, numbers
      legal={legalLine}
      lines={[
        // how to reach us; where the car is received, when that is not the seat
        [siteAddressIfDifferent(workshop.general) ? t("workOrder.print.siteAddress", { address: siteAddressIfDifferent(workshop.general) }) : "", g("workshopPhone")]
          .filter(Boolean)
          .join(" · "),
      ]}
      title={title}
      number={doc === "warranty" ? t("warranty.number", { number: order.number }) : order.number}
      meta={meta}
    />
  );
  const foot = (
    <div className="ps-foot">
      <MadeWith />
    </div>
  );

  if (doc === "warranty") {
    const covered = counted.filter((i) => !i.customerSupplied);
    const supplied = counted.filter((i) => i.customerSupplied);
    const key = w.profile.textKey;
    return (
      <div className="print-sheet ps-doc-warranty">
        {head}
        <section className="ps-badge">
          <Icon name="warranty" size={52} />
          {w.months ? (
            <div className="ps-months">
              <b>{w.months}</b>
              <small>{t("warranty.monthsLabel")}</small>
            </div>
          ) : (
            <div className="ps-months">
              <small>{t("workOrder.print.warrantyDocTitle")}</small>
            </div>
          )}
          <div className="ps-badge-facts">
            <div>
              <p className="ps-cap">{t("warranty.start")}</p>
              <b className="mono">{date(w.handover)}</b>
            </div>
            {w.until && (
              <div>
                <p className="ps-cap">{t("warranty.end")}</p>
                <b className="mono">{date(w.until)}</b>
              </div>
            )}
            {kmText && (
              <div>
                <p className="ps-cap">{t("warranty.km")}</p>
                <b className="mono">{kmText}</b>
              </div>
            )}
            {plate && (
              <div>
                <p className="ps-cap">{t("workOrder.vehicle")}</p>
                {plate}
              </div>
            )}
          </div>
        </section>
        {w.own && (
          <section className="ps-box">
            <p className="ps-cap">{t("warranty.terms")}</p>
            <p className="pre">{w.own}</p>
          </section>
        )}
        <section className="ps-box">
          <p className="ps-cap">{t("warranty.items")}</p>
          <table className="ps-items">
            <thead>
              <tr>
                <th>{t("workOrder.item.description")}</th>
                <th>{t("workOrder.item.partNumber")}</th>
                <th className="num">{t("workOrder.item.quantity")}</th>
              </tr>
            </thead>
            <tbody>
              {covered.map((i) => (
                <tr key={i.id}>
                  <td>{i.description}</td>
                  <td className="mono">{i.partNumber}</td>
                  <td className="num">
                    {i.quantity.toLocaleString(i18n.language)} {i.unit}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {supplied.length > 0 && <p className="ps-note">{t("warranty.suppliedNote", { items: supplied.map((i) => i.description).join(", ") })}</p>}
        </section>
        <section className="ps-box">
          <p className="ps-cap">{t("warranty.whatToDo")}</p>
          <p>{t(`legal.${key}.warrantySteps`)}</p>
        </section>
        <p className="ps-legal">{t(`legal.${key}.warrantyLaw`)}</p>
        <Signatures left={t("warranty.signWorkshop")} right={t("warranty.signCustomer")} />
        <div className="ps-cut">
          <span>{t("warranty.claimTitle")}</span>
        </div>
        <section className="ps-claim">
          <div>
            <p className="ps-cap">{t("warranty.claimNumber")}</p>
            <div className="mono ps-claim-no">{t("warranty.number", { number: order.number })}</div>
          </div>
          <div>
            <p className="ps-cap">{t("warranty.claimDate")}</p>
            <div className="ps-line" />
          </div>
          <div className="ps-wide">
            <p className="ps-cap">{t("warranty.claimFault")}</p>
            <div className="ps-line" />
            <div className="ps-line" />
          </div>
          <div>
            <p className="ps-cap">{t("warranty.claimRemedy")}</p>
            <div className="ps-checks">
              {(["repair", "redo", "discount", "withdraw"] as const).map((r) => (
                <span key={r}>
                  <i aria-hidden="true" />
                  {t(`warranty.remedy.${r}`)}
                </span>
              ))}
            </div>
          </div>
          <div>
            <p className="ps-cap">{t("warranty.claimDecision")}</p>
            <div className="ps-line" />
            <div className="ps-line" />
          </div>
        </section>
        {foot}
      </div>
    );
  }

  const quoted = doc === "quote" ? quotedLines(allItems) : [];
  const grandLabel = t(doc === "closed" ? "workOrder.print.toPay" : "workOrder.print.expectedTotal");
  return (
    <div className={`print-sheet ps-doc-${doc}`}>
      {head}

      <div className="ps-boxes">
        <section>
          <p className="ps-cap">{t("workOrder.vehicle")}</p>
          <KeyValues
            rows={[
              [t("vehicle.plateNumber"), plate],
              [t("vehicle.model"), vehicleName],
              [t("vehicle.vin"), doc === "quote" ? null : vehicle?.vin, "mono"],
              [t("vehicle.engineCode"), doc === "closed" ? vehicle?.engineCode : null, "mono"],
              [t("workOrder.odometerKm"), kmText, "mono"],
            ]}
          />
        </section>
        {doc === "quote" ? (
          <section>
            <p className="ps-cap">{customer?.name ? t("quote.page.greeting", { name: customer.name }) : t("quote.page.greetingNoName")}</p>
            <p className="ps-letter">{t("workOrder.print.quoteIntro")}</p>
          </section>
        ) : (
          <section>
            <p className="ps-cap">{t("workOrder.customer")}</p>
            <KeyValues
              rows={[
                [t("customer.name"), customer?.name ?? order.customerName],
                [t("customer.addressLine"), address],
                [t("customer.mobile"), phone, "mono"],
                [t("customer.email"), customer?.email],
                [t("customer.taxNumber"), customer?.taxNumber, "mono"],
              ]}
            />
          </section>
        )}
      </div>

      {order.customerRequest && doc !== "quote" && (
        <section className="ps-text">
          <p className="ps-cap">{t("workOrder.customerRequest")}</p>
          <p>{order.customerRequest}</p>
        </section>
      )}
      {order.findings && doc !== "intake" && (
        <section className="ps-text">
          <p className="ps-cap">{t("workOrder.findings")}</p>
          <p>{order.findings}</p>
        </section>
      )}
      {doc === "intake" && (
        <section className="ps-ready">
          <div>
            <p className="ps-cap">{t("workOrder.expectedReadyAt")}</p>
            {/* the intake receipt must show the expected completion date (HU 249/2004. 5. §) */}
            <div className="ps-big">{order.expectedReadyAt ? date(order.expectedReadyAt) : "–"}</div>
          </div>
          {damagePhotos > 0 && <p className="ps-photos">{t("workOrder.print.damagePhotos", { count: damagePhotos })}</p>}
        </section>
      )}
      {doc === "intake" && <PrintIntakeExtras extras={order.intakeExtras} />}
      {doc === "intake" && shots.length > 0 && (() => {
        const c = intakeCollage(shots);
        const first = shots[0]!.createdAt;
        const works = link && link.expiresAt > new Date().toISOString();
        return (
          <PrintCollage
            shots={c.shown}
            more={c.more}
            caption={<>
              {t("intake.print.caption", { count: shots.length, date: dateTime(first) })}
              {works && <> {t("intake.print.onLink")} <span className="mono">{intakeLink(link!.token).replace(/^https?:\/\//, "").slice(0, 40)}…</span></>}
            </>}
          />
        );
      })()}

      {items.length > 0 && (
        <table className="ps-items">
          <thead>
            <tr>
              <th>{t("workOrder.item.description")}</th>
              <th>{t("workOrder.item.partNumber")}</th>
              <th className="num">{t("workOrder.item.quantity")}</th>
              {prices && <th className="num">{t("workOrder.item.unitPrice")}</th>}
              {prices && <th className="num">{t("workOrder.item.vatRate")}</th>}
              {prices && <th className="num">{t("workOrder.net")}</th>}
              {prices && <th className="num">{t("workOrder.gross")}</th>}
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} className={doc === "quote" && i.approval === "declined" ? "ps-declined" : undefined}>
                <td>
                  <span className="ps-desc">{i.description}</span>
                  {i.customerSupplied && <span> ({t("workOrder.item.customerSuppliedShort")})</span>}
                  {i.approval && i.approval !== "none" && (doc === "quote" || i.approval === "pending") && (
                    <span className={`ps-mark ps-mark-${i.approval}`}>{t(`quote.approval.${i.approval}`)}</span>
                  )}
                </td>
                <td className="mono">{i.partNumber}</td>
                <td className="num">
                  {i.quantity.toLocaleString(i18n.language)} {i.unit}
                </td>
                {prices && (
                  <td className="num">
                    {money(i.unitPrice)} {t(`workOrder.priceMode.${i.priceMode}Short`)}
                  </td>
                )}
                {prices && <td className="num">{i.vatRate}%</td>}
                {prices && <td className="num">{money(i.net)}</td>}
                {prices && <td className="num">{money(i.gross)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {items.length > 0 && (
        <div className="ps-sum">
          <div className="ps-sum-left">
            {doc === "closed" && <Stamp label={t("workOrder.print.stampClosed")} when={dateTime(order.closedAt)} />}
            {doc === "quote" && answered?.answeredAt && (
              <Stamp
                tone={quoted.some((i) => i.approval === "pending") ? "orange" : "green"}
                label={t("workOrder.print.stampAnswered")}
                when={dateTime(answered.answeredAt)}
              />
            )}
            {doc === "closed" && answered?.answeredAt && (
              <p className="ps-note">
                {t("quote.printAnswered", { date: date(answered.answeredAt), via: t(`quote.via.${answered.channel}`) })}
                {answered.note && <> · {answered.note}</>}
              </p>
            )}
          </div>
          {prices && (
            <DocTotals
              rows={[
                { label: t("workOrder.net"), value: money(totals.net) },
                { label: t("workOrder.vat"), value: money(totals.vat) },
              ]}
              label={grandLabel}
              total={money(totals.gross)}
            />
          )}
        </div>
      )}

      {qr && (
        <section className="ps-qr">
          <div className="ps-qr-code" dangerouslySetInnerHTML={{ __html: renderSVG(qr.link, { border: 1 }) }} />
          <div>
            <div className="ps-qr-title">{t("workOrder.print.quoteQrTitle")}</div>
            <p>{t("workOrder.print.quoteQrText")}</p>
            <p className="mono ps-link">{qr.link}</p>
          </div>
        </section>
      )}
      {doc === "quote" && <p className="ps-note">{t("workOrder.print.quoteNote")}</p>}

      {order.notes && doc !== "quote" && (
        <section className="ps-text ps-text-plain">
          <p className="ps-cap">{t("workOrder.notes")}</p>
          <p>{order.notes}</p>
        </section>
      )}
      {g("printNote") && <p className="ps-note">{g("printNote")}</p>}

      {doc === "intake" && workshop.general.intakePaperPhoto === true && !phoneSig && (
        <section className="ps-qr ps-paper-qr">
          <div className="ps-qr-code" dangerouslySetInnerHTML={{ __html: renderSVG(paperLink(order.id), { border: 1 }) }} />
          <div>
            <div className="ps-qr-title">{t("paper.qrTitle")}</div>
            <p>{t("paper.qrText")}</p>
          </div>
        </section>
      )}
      {doc === "intake" && (
        <div className="ps-stamp-row">
          <Stamp tone="orange" label={t("workOrder.print.stampIntake")} when={dateTime(order.receivedAt)} />
        </div>
      )}

      {/* the statutory text and the signatures sit at the bottom of the page */}
      <div className="ps-bottom">
        {doc === "closed" && w.applies && (
          <section className="ps-legal ps-warranty">
            <strong>{t("workOrder.print.warrantyTitle")}</strong> {w.own ?? t(`legal.${w.profile.textKey}.warranty`)}{" "}
            {t("legal.handoverFacts", { handover: date(w.handover), km: kmText ?? "–" })}
            {w.until && <> · <strong>{t("legal.warrantyUntil", { until: date(w.until) })}</strong></>}
          </section>
        )}
        {doc === "intake" && <p className="ps-legal">{t(`legal.${w.profile.textKey}.intake`)}</p>}
        <Signatures
          left={t(doc === "closed" ? "workOrder.print.signHandover" : doc === "quote" ? "workOrder.print.signQuoteWorkshop" : "workOrder.print.signOrder")}
          right={t(doc === "closed" ? "workOrder.print.signHandoverWorkshop" : doc === "quote" ? "workOrder.print.signQuoteCustomer" : "workOrder.print.signReceiver")}
          leftAbove={phoneSig && <PrintSignature sig={phoneSig} when={dateTime(phoneSig.signedAt)} />}
        />
        {phoneSig && <p className="ps-legal ps-sig-note">{t("intake.print.hashNote", { hash: shortHash(phoneSig.contentHash) })}</p>}
        {foot}
      </div>
    </div>
  );
}

/** The phone signature image above the customer's signature line, with "telefonon aláírva <date time>". */
function PrintSignature({ sig, when }: { sig: WorkOrderSignature; when: string }) {
  const { t } = useTranslation();
  return (
    <span className="ps-phone-sig">
      {sig.imageUrl && <img src={sig.imageUrl} alt={t("signature.imageAlt", { name: sig.signerName })} />}
      <small className="mono">{t("intake.print.signedOnPhone", { date: when })}</small>
    </span>
  );
}

/** The handover signed on the phone, in the closed band. */
function HandoverNote({ sig, order, customer, shots }: { sig: WorkOrderSignature; order: WorkOrder; customer: Customer | null; shots: IntakeShot[] }) {
  const matches = useSignatureMatches(sig, order, customer, shots);
  return <SignedNote sig={sig} matches={matches} />;
}

/**
 * The new work order tells its page to open the signature pad once (the customer is still at the counter), and
 * whether the customer's e-mail waits for the end of the signing step.
 */
let autoSignFor: { id: string; send: boolean } | null = null;
function markAutoSign(id: string, send: boolean) {
  autoSignFor = { id, send };
}
function takeAutoSign(id: string): { sign: boolean; send: boolean } {
  if (autoSignFor?.id !== id) return { sign: false, send: false };
  const send = autoSignFor.send;
  autoSignFor = null;
  return { sign: true, send };
}

/** "Az átvételt a szerelő végzi": a big tick card under the responsible mechanic (new and open work order). */
function HandOverTick({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="wide hand-over">
      <div className={`choice-card hand-over-tick${checked ? " selected" : ""}`}>
        <label>
          <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
          <strong>{t("workOrder.intakeByMechanic")}</strong>
        </label>
        <Help id="workOrder.intakeByMechanic" />
      </div>
      {/* outside the label: the tick's name stays short (and never contains the odometer's name) */}
      <p className="muted small hand-over-note">{t(checked ? "workOrder.intakeByMechanicNote" : "workOrder.intakeByMechanicOff")}</p>
    </div>
  );
}

/** The new work order tells its page to print the intake receipt once (at a computer, the customer signs on paper). */
let autoPrintFor: string | null = null;
function markAutoPrint(id: string) {
  autoPrintFor = id;
}
function takeAutoPrint(id: string): boolean {
  if (autoPrintFor !== id) return false;
  autoPrintFor = null;
  return true;
}
